import React, { useState, useRef, useEffect, useCallback, startTransition } from 'react';
import axios from 'axios';
import TokenDisplay from './tokens/TokenDisplay';
import TokenWalletModal from './tokens/TokenWalletModal';
import AchievementPopup from './tokens/AchievementPopup';
import LiveKitVoiceInput from './LiveKitVoiceInput';
import AssignmentBanner from './AssignmentBanner';
import ChatMarkdown from './ChatMarkdown';
import FeedbackReaction from './FeedbackReaction';
import StudyCallButton from './StudyCallButton';
import posthog from '../utils/posthog-stub'; // FERPA: Using no-op stub until self-hosted PostHog is configured
import { BlockMath } from 'react-katex';
import 'katex/dist/katex.min.css';
import { motion, AnimatePresence } from 'framer-motion';
import FeatureTiers from './FeatureTiers';
import FeatureGate from './FeatureGate';
import Branding from './Branding';
import TappyLabsBranding from './TappyLabsBranding';
import { QRCodeSVG } from 'qrcode.react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useReadingSounds } from './ReadingSoundManager';
import ToastNotification from './ToastNotification';
import ConnectionLoadingState from './ConnectionLoadingState';
import ActiveSessionView from './ActiveSessionView'; // Phase 2 - 2025 orb design
import MobileControlBar from './MobileControlBar';
import MobileSessionHeader from './MobileSessionHeader';
import StudyCodeSheet from './StudyCodeSheet';
import JoinFriendModal from './JoinFriendModal';
import AddFriendModal from './AddFriendModal';
import FriendsOnlineWidget from './FriendsOnlineWidget';
import CollaborationCelebration from './CollaborationCelebration';
import TeachItBackView from './TeachItBackView';
import LearningSnapshot from './conversion/LearningSnapshot';
import CustomizeMiraPrompt from './conversion/CustomizeMiraPrompt';
import GuestMiniDashboard from './conversion/GuestMiniDashboard';
import PricingTiers from './PricingTiers';
import ClutchTimeModal from './ClutchTimeModal';
import ClutchTimeRadar from './ClutchTimeRadar';
import ClutchTimeSummary from './ClutchTimeSummary';
import ClutchTimeProactiveTrigger from './ClutchTimeProactiveTrigger';
import { useMobileDetect, useHaptics, useAutoHide } from './hooks';
// DataPacketKind not available in v1.15.13, will use numeric constants
import './VoiceTutor.css';
import './TutorMobile.css';
import './TutorInterface.css';
import './ClutchTime.css';

// SVG Icons - 2025 Professional Design (no emojis)
const Icons = {
  Mic: () => (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
      <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
      <line x1="12" x2="12" y1="19" y2="22"/>
    </svg>
  ),
  Keyboard: () => (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="2"/>
      <path d="M6 8h.01"/>
      <path d="M10 8h.01"/>
      <path d="M14 8h.01"/>
      <path d="M18 8h.01"/>
      <path d="M8 12h.01"/>
      <path d="M12 12h.01"/>
      <path d="M16 12h.01"/>
      <path d="M7 16h10"/>
    </svg>
  ),
  Phone: () => (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
    </svg>
  ),
  Check: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  ),
  Sun: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="4"/>
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41"/>
    </svg>
  ),
  Moon: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/>
    </svg>
  ),
  User: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
};

/**
 * SilentModeChat - Complete chat interface for Silent Mode (classroom use)
 * Calls /api/silent-chat directly - no LiveKit needed
 * Follows 2025 UX principles: Minimal, focused, no distractions
 */
/**
 * SilentModeChat - 2026 Premium Text Chat Experience
 *
 * Design Principles (Apple/Google/Discord/Notion level):
 * - Dark mode by default (Gen Z preference)
 * - Glassmorphism effects
 * - Streaming with smooth animations
 * - Mira avatar with presence indicator
 * - Quick action chips
 * - Suggested follow-ups
 * - Copy/share on messages
 * - Micro-interactions throughout
 */
const SilentModeChat = ({ userId, sessionId, anonymousId, onEndSession, onSessionLimitReached, onOpenClutchTime, clutchTimeActive, clutchTimeSubject, clutchTimeTopics, clutchTimeTestDate, onStudentMessage, headerAvatar }) => {
  const [message, setMessage] = React.useState('');
  const [messages, setMessages] = React.useState([]);
  const [isSending, setIsSending] = React.useState(false);
  const [isStreaming, setIsStreaming] = React.useState(false);
  const [copiedId, setCopiedId] = React.useState(null);
  // Tap-to-enlarge for homework photos (field-observed: students shoot
  // their homework and need to re-read it while Mira walks them through)
  const [lightboxImage, setLightboxImage] = React.useState(null);
  // Explain level. Field data: "Explain it simpler" is by far the
  // most-tapped chip (137 taps / 36 students) — students were re-requesting
  // the same adjustment every turn because it never stuck. This persists.
  const [explainLevel, setExplainLevel] = React.useState(
    () => localStorage.getItem('tappy_explain_level') || 'normal'
  );
  const setExplain = (level) => {
    setExplainLevel(level);
    localStorage.setItem('tappy_explain_level', level);
  };
  // Grade selection was removed (added friction, unproven benefit, and it
  // made Mira remark that topics were "advanced for a 7th grader"). Clear
  // any grade a previous version stored, including on shared devices.
  React.useEffect(() => {
    try {
      localStorage.removeItem('tappy_grade_level');
      localStorage.removeItem('tappy_grade_asked');
    } catch (e) { /* storage unavailable — nothing to clear */ }
  }, []);
  const clutchTimeAutoStarted = React.useRef(false);
  // No selectedImage state - we auto-send (Cash App style)
  const inputRef = React.useRef(null);
  const messagesEndRef = React.useRef(null);
  const textareaRef = React.useRef(null);
  const fileInputRef = React.useRef(null);
  const libraryInputRef = React.useRef(null);

  // Auto-scroll to bottom when new messages arrive
  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input on mount
  React.useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Close the photo lightbox with Escape (keyboard users / iPad keyboards)
  React.useEffect(() => {
    if (!lightboxImage) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setLightboxImage(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxImage]);

  // Auto-resize textarea
  React.useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      // Students paste whole LMS items with answer choices (the dominant
      // input shape in production). A fixed 120px cap hid most of it, so
      // they sent questions they couldn't see. Grow to ~1/3 of viewport.
      const cap = Math.max(120, Math.round(window.innerHeight * 0.33));
      textareaRef.current.style.height =
        Math.min(textareaRef.current.scrollHeight, cap) + 'px';
    }
  }, [message]);

  // Auto-start Clutch Time quiz when returning from setup
  React.useEffect(() => {
    if (clutchTimeActive && clutchTimeSubject && !clutchTimeAutoStarted.current) {
      clutchTimeAutoStarted.current = true;
      // Small delay to let the UI render first
      setTimeout(() => {
        handleSend(`Let's start studying for my ${clutchTimeSubject} test`);
      }, 300);
    }
  }, [clutchTimeActive, clutchTimeSubject]); // eslint-disable-line react-hooks/exhaustive-deps

  // Process an image from ANY source — camera, library, Cmd+V paste, or
  // drag-and-drop — then AUTO-SEND (Cash App style, zero friction).
  // Uses canvas to normalize ALL image formats (HEIC, PNG, BMP, etc.) to JPEG
  // for universal Gemini API compatibility across laptop and iPad/mobile
  const processImageFile = (file) => {
    if (!file || isSending) return;

    // Accept any image — iPad may not report HEIC type correctly
    if (file.type && !file.type.startsWith('image/')) return;

    // Use canvas to convert any format to JPEG — guarantees Gemini compatibility
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = async () => {
      // Cap at 2048px longest side — keeps payload well under Gemini's 20MB inline limit
      // while preserving enough detail for homework/handwriting recognition
      const MAX_DIM = 2048;
      let { width, height } = img;
      if (width > MAX_DIM || height > MAX_DIM) {
        const scale = MAX_DIM / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      // toDataURL('image/jpeg') works on every browser — converts HEIC, PNG, WebP, etc.
      const jpegDataUrl = canvas.toDataURL('image/jpeg', 0.85);
      URL.revokeObjectURL(objectUrl);

      const imageData = {
        preview: jpegDataUrl,
        base64: jpegDataUrl.split(',')[1],
      };

      // Auto-send immediately - no preview step (Cash App style)
      setIsSending(true);
      setIsStreaming(true);

      setMessages(prev => [
        ...prev,
        { role: 'user', content: 'Help me with this', image: imageData.preview, id: Date.now() },
        { role: 'assistant', content: '', isStreaming: true, id: Date.now() + 1 }
      ]);

      try {
        const response = await fetch('/api/silent-chat/vision', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            message: 'Help me with this',
            image_base64: imageData.base64,
            user_id: userId || 'anonymous',
            anonymous_id: anonymousId || '',
            session_id: sessionId || 'default',
            conversation_history: messages.map(m => ({ role: m.role, content: m.content })),
            explain_level: explainLevel
          })
        });

        if (!response.ok) {
          if (response.status === 403) {
            const errData = await response.json().catch(() => ({}));
            if (errData.error === 'session_limit_reached' && onSessionLimitReached) {
              setMessages(prev => prev.slice(0, -2));
              setIsSending(false);
              setIsStreaming(false);
              onSessionLimitReached({
                gateType: errData.gate_type,
                isAnonymous: errData.is_anonymous,
                sessionCount: errData.session_count,
                message: errData.message
              });
              return;
            }
          }
          throw new Error('Vision request failed');
        }

        // Stream the response
        const streamReader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await streamReader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                if (data.chunk) {
                  setMessages(prev => {
                    const updated = [...prev];
                    const lastMsg = updated[updated.length - 1];
                    if (lastMsg && lastMsg.role === 'assistant') {
                      lastMsg.content += data.chunk;
                    }
                    return updated;
                  });
                }
                if (data.done) {
                  setMessages(prev => {
                    const updated = [...prev];
                    const lastMsg = updated[updated.length - 1];
                    if (lastMsg) lastMsg.isStreaming = false;
                    return updated;
                  });
                  setIsStreaming(false);
                }
              } catch (e) { /* ignore */ }
            }
          }
        }
      } catch (error) {
        console.error('Vision error:', error);
        setMessages(prev => {
          const updated = [...prev];
          const lastMsg = updated[updated.length - 1];
          if (lastMsg && lastMsg.role === 'assistant') {
            lastMsg.content = "Couldn't read that. Try again?";
            lastMsg.isStreaming = false;
          }
          return updated;
        });
        setIsStreaming(false);
      }

      setIsSending(false);
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      console.error('Failed to load image for vision');
    };

    img.src = objectUrl;
  };

  // Camera / library file input
  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // Clear so the same file can be selected again
    if (file) processImageFile(file);
  };

  // Cmd+V a screenshot straight into the chat (Mac/iPad + keyboard) —
  // one action instead of the desktop file-browser detour.
  const handlePaste = (e) => {
    const items = e.clipboardData?.items || [];
    for (const item of items) {
      if (item.type && item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          e.preventDefault();
          processImageFile(file);
          return;
        }
      }
    }
  };

  // Drag a photo/screenshot anywhere onto the chat (Mac/iPad pointer)
  const [isDragging, setIsDragging] = React.useState(false);
  const dragDepth = React.useRef(0);
  const handleDragEnter = (e) => {
    if (e.dataTransfer?.types?.includes('Files')) {
      e.preventDefault();
      dragDepth.current += 1;
      setIsDragging(true);
    }
  };
  const handleDragOver = (e) => {
    if (e.dataTransfer?.types?.includes('Files')) e.preventDefault();
  };
  const handleDragLeave = () => {
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setIsDragging(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    dragDepth.current = 0;
    setIsDragging(false);
    const file = Array.from(e.dataTransfer?.files || []).find(
      (f) => !f.type || f.type.startsWith('image/')
    );
    if (file) processImageFile(file);
  };

  // Copy message to clipboard
  const handleCopy = async (content, msgId) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedId(msgId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleSend = async (customMessage) => {
    const textToSend = customMessage || message.trim();
    if (!textToSend || isSending) return;

    // Feed the orb's curiosity signal. It was only ever wired to voice
    // transcripts, so in text mode (~98% of usage) the orb sat at neutral.
    if (onStudentMessage) onStudentMessage(textToSend);

    if (!customMessage) setMessage('');
    setIsSending(true);
    setIsStreaming(true);

    // Add user message to UI immediately, then add empty assistant message for streaming
    setMessages(prev => [
      ...prev,
      { role: 'user', content: textToSend, id: Date.now() },
      { role: 'assistant', content: '', isStreaming: true, id: Date.now() + 1 }
    ]);

    try {
      const response = await fetch('/api/silent-chat/stream', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          message: textToSend,
          user_id: userId || 'anonymous',
          anonymous_id: anonymousId || '',
          session_id: sessionId || 'default',
          conversation_history: messages.map(m => ({ role: m.role, content: m.content })),
          // Sticky response calibration (see explainLevel state)
          explain_level: explainLevel,
          // Notebook grounding parity with voice mode: pass the opaque
          // share token when the student arrived via an assignment link
          // (server resolves it; roster fallback applies otherwise).
          ...(() => {
            try {
              const nbToken = new URLSearchParams(window.location.search).get('notebook_assignment');
              return nbToken ? { notebook_assignment_token: nbToken } : {};
            } catch (e) { return {}; }
          })(),
          ...(clutchTimeActive && clutchTimeSubject ? {
            clutch_time_subject: clutchTimeSubject,
            clutch_time_topics: clutchTimeTopics || [],
            clutch_time_test_date: clutchTimeTestDate || null
          } : {})
        })
      });

      if (!response.ok) {
        if (response.status === 403) {
          const errData = await response.json().catch(() => ({}));
          if (errData.error === 'session_limit_reached' && onSessionLimitReached) {
            // Remove the optimistic messages we just added
            setMessages(prev => prev.slice(0, -2));
            setIsSending(false);
            setIsStreaming(false);
            onSessionLimitReached({
              gateType: errData.gate_type,
              isAnonymous: errData.is_anonymous,
              sessionCount: errData.session_count,
              message: errData.message
            });
            return;
          }
        }
        throw new Error('Stream request failed');
      }

      // Read the SSE stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE events (lines ending with \n\n)
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));

              if (data.chunk) {
                setMessages(prev => {
                  const updated = [...prev];
                  const lastMsg = updated[updated.length - 1];
                  if (lastMsg && lastMsg.role === 'assistant') {
                    lastMsg.content += data.chunk;
                  }
                  return updated;
                });
              }

              if (data.done) {
                setMessages(prev => {
                  const updated = [...prev];
                  const lastMsg = updated[updated.length - 1];
                  if (lastMsg && lastMsg.role === 'assistant') {
                    lastMsg.isStreaming = false;
                  }
                  return updated;
                });
                setIsStreaming(false);
              }
            } catch (parseError) {
              console.warn('SSE parse error:', parseError);
            }
          }
        }
      }
    } catch (error) {
      console.error('Silent chat stream error:', error);
      setMessages(prev => {
        const updated = [...prev];
        const lastMsg = updated[updated.length - 1];
        if (lastMsg && lastMsg.role === 'assistant') {
          lastMsg.content = "That didn't go through — your message is safe.";
          lastMsg.isStreaming = false;
          // Keep the original text so Retry can resend it. Students paste
          // long LMS questions; making them retype one is where they quit.
          lastMsg.failed = true;
          lastMsg.retryText = textToSend;
        }
        return updated;
      });
      setIsStreaming(false);
    }

    setIsSending(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleEndSession = async () => {
    try {
      await fetch('/api/silent-chat/end', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          user_id: userId || 'anonymous',
          session_id: sessionId || 'default'
        })
      });
    } catch (error) {
      console.error('Error ending session:', error);
    }
    if (onEndSession) onEndSession();
  };

  return (
    <div
      className="silent-chat"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Drop-anywhere overlay (Mac/iPad pointer) */}
      {isDragging && (
        <div className="silent-chat__drop-overlay">
          <div className="silent-chat__drop-overlay-inner">
            📸 Drop your photo — Mira will take a look
          </div>
        </div>
      )}
      {/* Header - Mira presence */}
      <div className="silent-chat__header">
        {/* Mira Avatar with glow */}
        {headerAvatar || (
          <div className="silent-chat__avatar">
            <div className="silent-chat__avatar-circle">
              {isStreaming ? '✦' : 'M'}
            </div>
            <div className="silent-chat__avatar-online" />
          </div>
        )}
        <div className="silent-chat__header-info">
          <div className="silent-chat__header-name">Mira</div>
          <div className={`silent-chat__header-status ${isStreaming ? 'silent-chat__header-status--active' : ''}`}>
            {isStreaming ? (
              <>
                <span className="silent-chat__status-dot" />
                on it...
              </>
            ) : 'here rn'}
          </div>
        </div>
        {/* Clutch Time - quick access to test prep */}
        {onOpenClutchTime && (
          <button
            className="silent-chat__header-btn silent-chat__header-btn--clutch"
            onClick={onOpenClutchTime}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
            </svg>
            Test Prep
          </button>
        )}
        {/* End Session */}
        <button
          className="silent-chat__header-btn silent-chat__header-btn--end"
          onClick={handleEndSession}
        >
          End
        </button>
      </div>

      {/* Messages area */}
      <div className="silent-chat__messages">
        {/* Empty state - Cash App minimal */}
        {messages.length === 0 && (
          <div className="silent-chat__empty">
            <div className="silent-chat__empty-icon">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/>
                <circle cx="12" cy="13" r="3"/>
              </svg>
            </div>
            <p className="silent-chat__empty-title">Snap your homework</p>
            <p className="silent-chat__empty-subtitle">or type a question</p>
          </div>
        )}

        {/* Messages */}
        {messages.map((msg, idx) => (
          <div
            key={msg.id || idx}
            className={`silent-chat__msg-row silent-chat__msg-row--${msg.role === 'user' ? 'user' : 'mira'}`}
          >
            {/* Mira avatar for assistant messages */}
            {msg.role === 'assistant' && (
              <div className="silent-chat__mira-label">
                <div className="silent-chat__mira-mini-avatar">
                  {msg.isStreaming ? '✦' : 'M'}
                </div>
                <span className="silent-chat__mira-name">Mira</span>
              </div>
            )}

            {/* Message bubble */}
            <div
              className={`silent-chat__bubble silent-chat__bubble--${msg.role === 'user' ? 'user' : 'mira'}${msg.image ? ' silent-chat__bubble--has-image' : ''}`}
              style={{ minHeight: msg.isStreaming && !msg.content ? '24px' : undefined }}
            >
              {/* User image if present */}
              {msg.image && (
                <img
                  src={msg.image}
                  alt="Your photo — tap to enlarge"
                  className="silent-chat__image"
                  style={{ marginBottom: msg.content ? '10px' : '0' }}
                  role="button"
                  tabIndex={0}
                  onClick={() => setLightboxImage(msg.image)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setLightboxImage(msg.image); }}
                />
              )}
              {/* Message content with whitespace preserved */}
              {msg.content && (
                <div className={`silent-chat__bubble-content${msg.image ? ' silent-chat__bubble-content--with-image' : ''}`}>
                  {msg.role === 'assistant' ? <ChatMarkdown text={msg.content} /> : msg.content}
                </div>
              )}

              {/* Streaming cursor */}
              {msg.isStreaming && (
                <span className="silent-chat__cursor" />
              )}
            </div>

            {/* Message actions for assistant messages */}
            {msg.role === 'assistant' && !msg.isStreaming && msg.content && (
              <div className="silent-chat__msg-actions">
                {msg.failed ? (
                  <button
                    className="silent-chat__retry-btn"
                    onClick={() => {
                      const text = msg.retryText;
                      // Drop the failed reply and its user message; the
                      // resend re-adds both.
                      setMessages((prev) => prev.slice(0, -2));
                      handleSend(text);
                    }}
                  >
                    ↻ Retry
                  </button>
                ) : (
                  <button
                    className={`silent-chat__copy-btn${copiedId === msg.id ? ' silent-chat__copy-btn--copied' : ''}`}
                    onClick={() => handleCopy(msg.content, msg.id)}
                  >
                    {copiedId === msg.id ? '✓ Copied' : '📋 Copy'}
                  </button>
                )}
              </div>
            )}
          </div>
        ))}

        <div ref={messagesEndRef} />
      </div>

      {/* Input area - Cash App style: clean, prominent camera */}
      <div className="silent-chat__input-area">
        {/* Quick-reply chips — composing the question is the hard part for
            MS/HS students; one tap beats a blank text field. */}
        {!isSending && !isStreaming && (
          <div className="silent-chat__chips">
            {[
              'Quiz me 🔥',
              'Explain it simpler',
              'What would my teacher ask?',
              'Show me an example',
            ].map((chip) => (
              <button
                key={chip}
                type="button"
                className="silent-chat__chip"
                onClick={() => {
                  // "Explain it simpler" now STICKS: it re-explains AND
                  // sets the level, so the student stops re-asking every
                  // turn (the single most-repeated action in prod data).
                  if (chip === 'Explain it simpler') setExplain('simpler');
                  handleSend(chip);
                }}
              >
                {chip}
              </button>
            ))}
          </div>
        )}

        {/* Active calibration pill — visible and clearable, so the student
            always knows why answers changed and can undo it. */}
        {explainLevel !== 'normal' && (
          <div className="silent-chat__level-pill">
            <span>
              {explainLevel === 'simpler'
                ? 'Keeping it simple'
                : 'Giving more detail'}
            </span>
            <button type="button" onClick={() => setExplain('normal')}>
              Undo
            </button>
          </div>
        )}
        <div className="silent-chat__input-row">
          {/* Camera FAB - prominent, one tap to snap & auto-send */}
          <button
            className="silent-chat__camera-btn"
            title="Take a photo of your homework"
            onClick={() => fileInputRef.current?.click()}
            disabled={isSending}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/>
              <circle cx="12" cy="13" r="3"/>
            </svg>
          </button>

          {/* Camera-roll path (capture-direct camera skips the chooser) */}
          <button
            className="silent-chat__library-btn"
            title="Choose from your photos"
            aria-label="Choose a photo from your library"
            onClick={() => libraryInputRef.current?.click()}
            disabled={isSending}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
              <circle cx="8.5" cy="8.5" r="1.5"/>
              <path d="m21 15-5-5L5 21"/>
            </svg>
          </button>

          {/* Text input with embedded send */}
          <div className="silent-chat__input-wrapper">
            <textarea
              ref={(el) => { inputRef.current = el; textareaRef.current = el; }}
              className="silent-chat__textarea"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              placeholder="Ask anything..."
              disabled={isSending}
              rows={1}
            />
            <button
              className={`silent-chat__send-btn ${message.trim() ? 'silent-chat__send-btn--active' : 'silent-chat__send-btn--inactive'}`}
              onClick={() => handleSend()}
              disabled={!message.trim() || isSending}
            >
              {isSending ? (
                <div className="silent-chat__send-spinner" />
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m22 2-7 20-4-9-9-4Z"/>
                  <path d="M22 2 11 13"/>
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Hidden inputs: capture="environment" opens the rear camera
          DIRECTLY on phones/tablets (skips the camera-or-library sheet —
          one tap = shutter open); desktop browsers ignore it and show the
          file dialog. The second input (no capture) is the camera-roll
          path behind the small library button. */}
      <input
        type="file"
        ref={fileInputRef}
        accept="image/*"
        capture="environment"
        onChange={handlePhotoSelect}
        style={{ display: 'none' }}
      />
      <input
        type="file"
        ref={libraryInputRef}
        accept="image/*"
        onChange={handlePhotoSelect}
        style={{ display: 'none' }}
      />

      {/* Photo lightbox — tap thumbnail to study the full homework photo.
          Tap anywhere (or Esc / ✕) to dismiss. */}
      {lightboxImage && (
        <div
          className="silent-chat__lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Enlarged photo"
          onClick={() => setLightboxImage(null)}
        >
          <button
            className="silent-chat__lightbox-close"
            aria-label="Close enlarged photo"
            onClick={() => setLightboxImage(null)}
          >
            ✕
          </button>
          <img
            src={lightboxImage}
            alt="Your photo, enlarged"
            className="silent-chat__lightbox-img"
          />
        </div>
      )}
    </div>
  );
};


function TutorInterface({
  userTier = 'guest',
  hasFeatureAccess,
  user,
  onVisualizationRequest,
  onLogout,                        // Logout handler from App.js
  classroomMode = false,           // NEW: Enable classroom mode (text-only)
  classroomSessionId = null        // NEW: Classroom session ID for DB logging
}) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(`session-${Date.now()}`);
  const [studentState, setStudentState] = useState([50, 70, 50, 0]);
  const [tokenBalance, setTokenBalance] = useState(0);
  const [streakDays, setStreakDays] = useState(0);
  const [achievement, setAchievement] = useState(null);
  // Mira customization (from token shop purchases)
  const [miraOrbColor, setMiraOrbColor] = useState('default');
  const [miraGlowEffect, setMiraGlowEffect] = useState('default');
  const [miraEyeStyle, setMiraEyeStyle] = useState('default');
  // Derive authentication from user prop (App.js is source of truth)
  const isAuthenticated = Boolean(user && user.id);
  const username = user?.username || user?.name || '';
  // const [showNeoSetup, setShowNeoSetup] = useState(false); // Removed
  const [tokensEarnedThisSession, setTokensEarnedThisSession] = useState(0);
  const [sessionInteractionCount, setSessionInteractionCount] = useState(0);
  const [learningStyle, setLearningStyle] = useState('visual');
  const [learningStyleWeights, setLearningStyleWeights] = useState({
    visual: 0.25,
    auditory: 0.25,
    kinesthetic: 0.25,
    'reading/writing': 0.25
  });
  // The following state variables are deprecated and will be removed in a future version
  // They are only kept here to avoid breaking existing code
  const [showArtifactPanel, setShowArtifactPanel] = useState(false);
  const [activeArtifactTab, setActiveArtifactTab] = useState('visualization');
  const [canvasInstructions, setCanvasInstructions] = useState(null);
  const [reactComponent, setReactComponent] = useState(null);
  const [lastVisualizationSource, setLastVisualizationSource] = useState('text');
  const [artifactLoading, setArtifactLoading] = useState(false);
  const [artifact, setArtifact] = useState(null);
  
  const [showStepByStep, setShowStepByStep] = useState(false);
  const [breakthroughDetected, setBreakthroughDetected] = useState(false);
  const [studentNotes, setStudentNotes] = useState('');
  const [milestones, setMilestones] = useState([]);
  const [highContrastMode, setHighContrastMode] = useState(false);
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  // const [showSessionFeedback, setShowSessionFeedback] = useState(false); // Removed

  // Connection state machine to prevent race conditions
  const [connectionState, setConnectionState] = useState('idle'); // 'idle', 'connecting', 'connected', 'disconnecting', 'error'
  const connectionStateRef = useRef('idle'); // Ref to track current state for timeout callbacks (avoids stale closure)

  // Keep ref in sync with state (for timeout callbacks to read current value)
  useEffect(() => {
    connectionStateRef.current = connectionState;
  }, [connectionState]);

  const [isCallActive, setIsCallActive] = useState(false); // Track if the call is active (box clicked) - DEPRECATED, use connectionState
  const [voiceComponentInitialized, setVoiceComponentInitialized] = useState(false); // Track if voice component has been initialized
  const [isMuted, setIsMuted] = useState(false); // Track if the mic is muted
  const [isSilentMode, setIsSilentMode] = useState(false); // Silent Mode: text-only, no voice (for classroom use)

  // PREVIEW SWITCH — "focus" chat layout, opt-in per device. Students see
  // nothing different unless this device opted in. ?layout=focus turns it
  // on (remembered), ?layout=classic turns it off.
  // Why: on Chromebooks and tablets (where most student work happens) the
  // Silent Mode page grew taller than the screen, so the input sat below
  // the fold, a fixed footer covered the bottom, and Mira appeared twice
  // (orb above, "M" avatar inside). Focus layout fits the chat to the
  // screen, pins the input, and makes the orb Mira's avatar.
  const [focusLayout] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search).get('layout');
      if (p === 'focus') localStorage.setItem('tappy_layout', 'focus');
      if (p === 'classic') localStorage.removeItem('tappy_layout');
      return localStorage.getItem('tappy_layout') === 'focus';
    } catch (e) { return false; }
  });
  const [sessionLanguage, setSessionLanguage] = useState('en'); // School-enabled bilingual support
  const clutchTimeLaunchedFromSilent = useRef(false); // Track if Clutch Time was opened from Silent Mode

  // FaceTime with Mira - Active session state (Phase 2)
  const [agentState, setAgentState] = useState('idle'); // 'idle' | 'listening' | 'thinking' | 'speaking'

  // 2026 Curiosity Ambient Feedback - continuous orb energy (Part 5 of TUTOR_INTERFACE_MOBILE_SOCIAL_REDESIGN.md)
  // Affects orb physics: pulse depth, glow warmth, animation responsiveness
  // No explicit UI - students FEEL it, they don't see numbers
  const [curiosityMomentum, setCuriosityMomentum] = useState(0.5); // 0.0-1.0, starts neutral
  const lastCuriosityUpdateRef = useRef(Date.now()); // For EMA smoothing
  const [sessionStartTime, setSessionStartTime] = useState(null); // Timestamp when session started
  const [friendsInSession, setFriendsInSession] = useState([]); // Friends currently in voice session
  const [friendsWhoStudied, setFriendsWhoStudied] = useState([]); // Friends who studied with us (persists even after they leave)
  const [friendParticipantsCount, setFriendParticipantsCount] = useState(0); // Track friend participants for multi-friend button
  const [videoCollaborationEnabled, setVideoCollaborationEnabled] = useState(false); // Track if video collaboration panel should show
  const [shouldMinimizeVideoPanel, setShouldMinimizeVideoPanel] = useState(false); // Auto-minimize video panel when code toast appears
  const [isScreenSharing, setIsScreenSharing] = useState(false); // Track screen sharing state for toggle button
  const [showJoinFriendModal, setShowJoinFriendModal] = useState(false); // Join friend session modal
  const [showAddFriendModal, setShowAddFriendModal] = useState(false); // Add friend by code modal
  const [showStudyCodeSheet, setShowStudyCodeSheet] = useState(false); // 2025 Study code sharing sheet
  const [studyCodeData, setStudyCodeData] = useState(null); // { code, expiresAt, joinUrl }
  const [showUserMenu, setShowUserMenu] = useState(false); // User dropdown menu (2025 UX - for 6-12 graders)
  const [showTokenWallet, setShowTokenWallet] = useState(false); // Token wallet modal
  const [onlineFriends, setOnlineFriends] = useState([]); // Friends online (for ActiveSessionView - visible without dropdown)
  const [showTeachItBack, setShowTeachItBack] = useState(false); // Teach It Back mode - Feynman technique
  const [teachItBackActive, setTeachItBackActive] = useState(false); // Track if currently in teach_it_back voice session
  const [currentTeachTopic, setCurrentTeachTopic] = useState(null); // Topic being taught in teach_it_back mode

  // Clutch Time - Exam prep mode (December 2025)
  const [showClutchTimeModal, setShowClutchTimeModal] = useState(false); // Entry modal
  const [showClutchTimeRadar, setShowClutchTimeRadar] = useState(false); // Radar view
  const [showClutchTimeSummary, setShowClutchTimeSummary] = useState(false); // Session summary
  const [clutchTimeSubject, setClutchTimeSubject] = useState(''); // Current subject
  const [clutchTimeTestDate, setClutchTimeTestDate] = useState(''); // Test date
  const [clutchTimeSessionId, setClutchTimeSessionId] = useState(null); // Active session ID
  const [clutchTimeActive, setClutchTimeActive] = useState(false); // Track if currently in clutch_time voice session
  const [clutchTimeTopics, setClutchTimeTopics] = useState([]); // Weak topics from radar
  const [clutchTimeImportedTopics, setClutchTimeImportedTopics] = useState([]); // Topics extracted from study guide (Phase 1.5)
  const [clutchTimeImportSource, setClutchTimeImportSource] = useState('manual'); // How topics were imported
  const [pendingScreenShare, setPendingScreenShare] = useState(false); // Trigger screen share after voice connection

  // Session summary modal (Duolingo-style token celebration)
  const [showSessionSummary, setShowSessionSummary] = useState(false);
  const [sessionSummaryData, setSessionSummaryData] = useState(null);

  // Toast notification state
  const [toastMessage, setToastMessage] = useState(null);
  const [toastType, setToastType] = useState('info');
  const [showToast, setShowToast] = useState(false);
  const [toastActionButton, setToastActionButton] = useState(null);

  // Dark mode state - system preference with manual override
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Check localStorage first for user preference
    const savedTheme = localStorage.getItem('tappy_theme');
    if (savedTheme) return savedTheme === 'dark';
    // Fall back to system preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      // Only update if user hasn't set a manual preference
      if (!localStorage.getItem('tappy_theme')) {
        setIsDarkMode(e.matches);
      }
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Apply theme to document
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
    // Also set on body for broader compatibility
    document.body.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  // Toggle theme function (for UI button)
  const toggleTheme = () => {
    const newTheme = !isDarkMode;
    setIsDarkMode(newTheme);
    localStorage.setItem('tappy_theme', newTheme ? 'dark' : 'light');
  };

  // Feature flags - fetched from server
  const [featureFlags, setFeatureFlags] = useState({
    classroom_mode: false,
    bounties: false,
    neo_model: false
  });

  // Mode state - track current mode (voice or classroom)
  // Load from localStorage or default to voice
  const [currentMode, setCurrentMode] = useState(() => {
    if (classroomMode) return 'classroom';
    const savedMode = localStorage.getItem('tappy_preferred_mode');
    return savedMode || 'voice';
  });

  // Save mode preference whenever it changes
  useEffect(() => {
    localStorage.setItem('tappy_preferred_mode', currentMode);
  }, [currentMode]);

  // Fetch feature flags on mount
  useEffect(() => {
    const fetchFeatureFlags = async () => {
      try {
        const response = await axios.get('/api/feature-flags');
        setFeatureFlags(response.data);
      } catch (error) {
        console.error('Failed to fetch feature flags:', error);
        // Default to all flags disabled on error
      }
    };
    fetchFeatureFlags();
  }, []);

  // Show screen share prompt after voice connection when pendingScreenShare is set
  // Used by Clutch Time's "Share your screen" flow
  // Note: getDisplayMedia requires direct user gesture, so we show a prompt instead of auto-triggering
  const [showScreenSharePrompt, setShowScreenSharePrompt] = useState(false);

  useEffect(() => {
    if (connectionState === 'connected' && pendingScreenShare) {
      // Show prompt for user to click (browser requires user gesture for screen share)
      setShowScreenSharePrompt(true);
      setPendingScreenShare(false);
    }
  }, [connectionState, pendingScreenShare]);

  // Handle the actual screen share trigger from user click
  const handleScreenSharePromptClick = async () => {
    setShowScreenSharePrompt(false);
    try {
      const roomInfo = voiceInputRef.current?.getRoomInfo?.();
      if (roomInfo?.localParticipant) {
        await roomInfo.localParticipant.setScreenShareEnabled(true);
        setIsScreenSharing(true);
        showToastNotification('Screen sharing started! Mira can now see your homework.', 'success');
      }
    } catch (error) {
      console.error('Screen share error:', error);
      if (error.name === 'NotAllowedError') {
        showToastNotification('Screen sharing permission denied', 'error');
      } else {
        showToastNotification('Screen sharing failed. Please try again.', 'error');
      }
    }
  };

  // Track current room name for presence updates
  const [currentRoomName, setCurrentRoomName] = useState(null);

  // Session presence tracking for Friends Online feature
  // - User shows in friends list when on platform (active: true)
  // - Avatar glows green only when in Mira session (invite_token present)
  const lastPresenceRef = useRef({ inviteToken: null, subject: null, sent: false });
  const presenceTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!user) return;

    // FERPA-compliant presence system:
    // - active: true = user is on the platform (shows in friends list)
    // - invite_token present = user is in a Mira session (avatar glows green, friends can join)
    const isInMiraSession = connectionState === 'connected';
    const inviteToken = isInMiraSession ? (currentRoomName || null) : null;
    const subject = isInMiraSession ? 'Studying with Mira' : 'Online';

    // Skip if nothing meaningful changed (prevents duplicate API calls)
    const lastState = lastPresenceRef.current;
    if (lastState.sent && lastState.inviteToken === inviteToken && lastState.subject === subject) {
      return;
    }

    // Clear any pending timeout
    if (presenceTimeoutRef.current) {
      clearTimeout(presenceTimeoutRef.current);
    }

    // Debounce presence updates to prevent rapid toggling during connection
    presenceTimeoutRef.current = setTimeout(async () => {
      if (!isMountedRef.current) return;

      try {
        await axios.post('/api/voice/session/set-presence', {
          active: true,  // Always active when on platform
          subject,
          allow_friends: true,
          invite_token: inviteToken  // Only set when in Mira session (enables glow + join)
        }, { withCredentials: true });

        // Track what we sent to avoid duplicates
        lastPresenceRef.current = { inviteToken, subject, sent: true };
        console.log(`🎯 Presence: ${isInMiraSession ? 'IN SESSION (glow)' : 'ONLINE (no glow)'}`);
      } catch (error) {
        if (error?.response?.status !== 401) {
          console.error('🎯 Failed to update session presence:', error);
        }
      }
    }, 300); // 300ms debounce

    // Cleanup pending timeout on unmount
    return () => {
      if (presenceTimeoutRef.current) {
        clearTimeout(presenceTimeoutRef.current);
      }
    };
  }, [connectionState, user, currentRoomName]);

  // Separate effect for sending offline on unmount
  useEffect(() => {
    return () => {
      // Send offline status when component unmounts (user leaves page)
      if (user && lastPresenceRef.current.sent) {
        // Use sendBeacon for reliable delivery on unmount
        const data = JSON.stringify({
          active: false,
          subject: 'Offline',
          allow_friends: true,
          invite_token: null
        });
        navigator.sendBeacon('/api/voice/session/set-presence', new Blob([data], { type: 'application/json' }));
      }
    };
  }, [user]);

  // Fetch Mira customization settings from token balance API
  // This loads the user's purchased orb colors, glow effects, etc.
  useEffect(() => {
    if (!user) return;

    const fetchMiraCustomization = async () => {
      try {
        const response = await axios.get('/api/tokens/balance', { withCredentials: true });
        if (response.data) {
          setMiraOrbColor(response.data.mira_orb_color || 'default');
          setMiraGlowEffect(response.data.mira_glow_effect || 'default');
          setMiraEyeStyle(response.data.mira_eye_style || 'default');
          // Also update token balance and streak while we're at it
          if (response.data.balance !== undefined) {
            setTokenBalance(response.data.balance);
          }
          if (response.data.streak_days !== undefined) {
            setStreakDays(response.data.streak_days);
          }
        }
      } catch (error) {
        // Silent fail - user just gets default Mira appearance
        console.debug('Mira customization fetch failed:', error);
      }
    };

    fetchMiraCustomization();
  }, [user]);

  // Online friends data is now managed by FriendsOnlineWidget (single source of truth)
  // FriendsOnlineWidget calls onFriendsUpdate callback when data changes
  // This eliminates duplicate polling - was previously 2x API calls every 30s
  const handleFriendsUpdate = useCallback((friends) => {
    setOnlineFriends(friends);
  }, []);

  // Track which friend we're joining (for loading state)
  const [joiningFriendId, setJoiningFriendId] = useState(null);

  // FERPA-compliant friend join handler
  // Uses secure endpoint that verifies FriendConnection before generating session token
  const handleJoinFriendSession = useCallback(async (friend) => {
    if (!friend?.id || !friend.can_join) {
      return;
    }

    // Prevent double-clicks
    if (joiningFriendId) return;

    setJoiningFriendId(friend.id);

    try {
      // Call secure endpoint - FriendConnection serves as pre-approval (replaces 4-digit code)
      const response = await axios.post(`/api/friends/request-join/${friend.id}`, {}, {
        withCredentials: true
      });

      if (response.data.success && response.data.session_token) {
        // Navigate with secure one-time token (not raw room name)
        window.location.href = `/join/${response.data.session_token}`;
      } else {
        console.error('Failed to get session token:', response.data.error);
        setToastMessage(response.data.error || 'Could not join friend\'s session');
        setToastType('error');
        setShowToast(true);
        setJoiningFriendId(null);
      }
    } catch (error) {
      console.error('Error joining friend session:', error);
      const errorMessage = error.response?.data?.error || 'Failed to join session';
      setToastMessage(errorMessage);
      setToastType('error');
      setShowToast(true);
      setJoiningFriendId(null);
    }
  }, [joiningFriendId]);

  // Share with Parent modal state
  const [showShareWithParentModal, setShowShareWithParentModal] = useState(false);
  const [shareCode, setShareCode] = useState('');
  const [shareTier, setShareTier] = useState('unlimited');
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState(null);

  // Viral collaboration states
  const [showAchievementToast, setShowAchievementToast] = useState(false);
  const [struggleDetection, setStruggleDetection] = useState({
    consecutiveWrongAnswers: 0,
    timeOnCurrentProblem: 0,
    frustrationKeywords: 0,
    shouldShowHelp: false
  });
  const [lastResponseTime, setLastResponseTime] = useState(Date.now());
  const [guestHelp, setGuestHelp] = useState(null);
  const [checkingForHelp, setCheckingForHelp] = useState(false);
  const audioRef = useRef(null);
  const messagesEndRef = useRef(null);
  const voiceInputRef = useRef(null);

  // React Router hooks for navigation and location
  const location = useLocation();
  const navigate = useNavigate();

  // Guest mode detection and state
  const isGuest = !user || userTier === 'guest' || userTier === FeatureTiers.TIERS.GUEST;
  const isAnonymous = !user;  // Truly anonymous vs authenticated free account
  const [anonymousId, setAnonymousId] = useState('');
  const [showProgressProtection, setShowProgressProtection] = useState(false);
  const [progressProtectionDismissed, setProgressProtectionDismissed] = useState(false); // Prevents re-showing after Skip
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [billingFrequency, setBillingFrequency] = useState('annual');

  // Conversion funnel states
  const [showLearningSnapshot, setShowLearningSnapshot] = useState(false);
  const [showCustomizeMiraPrompt, setShowCustomizeMiraPrompt] = useState(false);
  const [showMiniDashboard, setShowMiniDashboard] = useState(false);
  const [guestSessionCount, setGuestSessionCount] = useState(0);
  const [gateType, setGateType] = useState('none'); // 'none', 'create_account', 'subscribe'
  const [sessionTopics, setSessionTopics] = useState([]); // Topics discussed in current session
  const [sessionCognitiveState, setSessionCognitiveState] = useState('building_confidence'); // User's learning state

  // Guest learning profile (accumulated investment across sessions)
  const [guestProfile, setGuestProfile] = useState({
    totalSessions: 0,
    streakDays: 0,
    longestStreak: 0,
    topicsCovered: [],
    totalMinutes: 0,
    totalInteractions: 0
  });
  const [guestProfileLoaded, setGuestProfileLoaded] = useState(false);

  // Reading sound effects integration
  const { isReady: soundsReady, soundManager, triggerStoryAtmosphere, triggerActionSound, triggerTransition, stopAllSounds } = useReadingSounds();

  // Mobile-first UX hooks (2025 design)
  const device = useMobileDetect();
  const haptics = useHaptics();
  const controlsAutoHide = useAutoHide({
    delay: 3000,
    initialVisible: true,
    disabled: true, // Keep controls always visible for voice sessions
  });

  // Toast notification helper (memoized for stable reference)
  const showToastNotification = useCallback((message, type = 'info', actionButton = null) => {
    setToastMessage(message);
    setToastType(type);
    setToastActionButton(actionButton);
    setShowToast(true);
  }, []);

  const handleToastClose = () => {
    setShowToast(false);
    setToastMessage(null);
    setToastActionButton(null);
  };

  // Handle direct Stripe checkout from in-session upgrade modal
  const handleDirectCheckout = async (tier, billing) => {
    setCheckoutLoading(true);
    setCheckoutError('');
    try {
      const response = await axios.post('/api/payment/create-checkout-session', {
        tier,
        billing_frequency: billing
      }, { withCredentials: true });
      if (response.data && response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        setCheckoutError('Unable to start checkout. Please try again.');
        setCheckoutLoading(false);
      }
    } catch (err) {
      const msg = err.response?.data?.error || 'Checkout failed. Please try again.';
      setCheckoutError(msg);
      setCheckoutLoading(false);
    }
  };

  // Handle guest payment checkout
  const handleGuestCheckout = async (paymentMethod = 'card') => {
    setPaymentLoading(true);
    setPaymentError(null);

    try {
      // Track payment attempt
      if (window.posthog) {
        posthog.capture('guest_payment_initiated', {
          payment_method: paymentMethod,
          tier: shareTier,
          anonymous_id: anonymousId,
          tokens_earned: tokensEarnedThisSession,
          session_interactions: sessionInteractionCount
        });
      }

      // Get CSRF token from cookie
      const getCookie = (name) => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
        return null;
      };

      let csrfToken = getCookie('csrf_token');

      // If no CSRF token for guest, get one first
      if (!csrfToken && isGuest) {
        try {
          await fetch('/api/guest/csrf-token', {
            method: 'GET',
            credentials: 'include'
          });
          // Token should now be in cookie
          csrfToken = getCookie('csrf_token');
        } catch (error) {
          console.warn('Failed to get CSRF token:', error);
        }
      }

      // Call existing Stripe checkout endpoint with guest metadata
      const headers = {
        'Content-Type': 'application/json'
      };

      // Add CSRF token if available
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      }

      const response = await fetch('/api/payment/create-checkout-session', {
        method: 'POST',
        credentials: 'include',
        headers: headers,
        body: JSON.stringify({
          tier: shareTier,
          billing_frequency: 'monthly', // Monthly for lowest friction guest conversion
          conversion_source: 'feature_gate_unlock',
          friend_context: {
            user_type: 'guest',
            anonymous_id: anonymousId,
            tokens_earned: tokensEarnedThisSession,
            session_interactions: sessionInteractionCount,
            payment_method: paymentMethod
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const { checkout_url } = await response.json();

      // Redirect to Stripe Checkout (supports Apple Pay, Google Pay, Cash App, cards)
      window.location.href = checkout_url;

    } catch (error) {
      console.error('Checkout error:', error);
      setPaymentError('Could not start checkout. Please try again.');
      setPaymentLoading(false);

      if (window.posthog) {
        posthog.capture('guest_payment_error', {
          error: error.message,
          tier: shareTier,
          anonymous_id: anonymousId
        });
      }
    }
  };

  // Viral collaboration functions

  // Complete the end session flow (called after study buddy modal or directly)
  const completeEndSession = useCallback(async (sharedRoomName, currentSessionId, wasConnected, studyPartnerData = null) => {
    // FIRST: Update state to show disconnecting UI
    setConnectionState('disconnecting');
    connectionStateRef.current = 'disconnecting';
    setIsCallActive(false);
    setVideoCollaborationEnabled(false);
    setIsScreenSharing(false);
    setAgentState('idle');
    // Reset Silent Mode when session ends
    setIsSilentMode(false);
    // Reset Clutch Time state when session ends
    setClutchTimeActive(false);
    setClutchTimeTopics([]);
    setClutchTimeImportedTopics([]);
    setClutchTimeImportSource('manual');

    // THEN: Call disconnect
    try {
      if (voiceInputRef.current && voiceInputRef.current.disconnect) {
        await voiceInputRef.current.disconnect();
      }
    } catch (error) {
      console.error('Disconnect error:', error);
    }

    // Wait for cleanup (backend processes tokens during this time)
    await new Promise(resolve => setTimeout(resolve, wasConnected ? 20000 : 2000));
    setConnectionState('idle');

    // Fetch session summary with tokens for authenticated users
    if (isAuthenticated && wasConnected) {
      try {
        // Try fetching session summary (may need a few retries as backend processes)
        let summaryData = null;
        let retries = 3;

        while (retries > 0 && !summaryData) {
          // Pass room name for study buddy lookup (shared between host & friend)
          // Falls back to client sessionId for token tracking
          const summaryId = sharedRoomName || currentSessionId;

          const response = await axios.get(`/api/voice/session-summary/${summaryId}`, {
            withCredentials: true
          });

          if (response.data.success && response.data.status === 'complete') {
            summaryData = response.data;
            break;
          }

          // Wait and retry if still processing
          if (response.data.status === 'processing') {
            await new Promise(resolve => setTimeout(resolve, 2000));
            retries--;
          } else {
            break;
          }
        }

        // Show token celebration (study buddy already handled via real-time modal)
        const hasTokens = summaryData && summaryData.tokens_earned > 0;

        if (hasTokens) {
          // Calculate session duration
          const sessionDurationSeconds = sessionStartTime
            ? Math.floor((Date.now() - sessionStartTime) / 1000)
            : 0;

          setSessionSummaryData({
            ...summaryData,
            sessionDuration: sessionDurationSeconds,
            studyPartner: studyPartnerData, // Pass to CollaborationCelebration for inline study buddy toggle
            sessionId: sharedRoomName || currentSessionId
          });
          setShowSessionSummary(true);
        } else {
          // No tokens - show simple toast
          showToastNotification('Session ended', 'info');
        }
      } catch (error) {
        console.error('Error fetching session summary:', error);
        showToastNotification('Session ended', 'info');
      }
    } else if (isGuest && wasConnected) {
      // Guest session ended - show Learning Snapshot for conversion funnel
      // Increment local session count
      const sessionCountKey = user
        ? `mira_session_count_free:${user.id}`
        : `mira_session_count_anon:${anonymousId}`;
      const newCount = guestSessionCount + 1;
      localStorage.setItem(sessionCountKey, newCount.toString());
      setGuestSessionCount(newCount);

      // Calculate session duration in minutes
      const sessionDurationMinutes = sessionStartTime
        ? Math.floor((Date.now() - sessionStartTime) / 60000)
        : 0;

      // Save accumulated learning profile to backend for persistence
      try {
        await fetch('/api/guest/learning-profile', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            anonymous_id: anonymousId,
            total_sessions: guestProfile.totalSessions + 1,
            total_interactions: guestProfile.totalInteractions + sessionInteractionCount,
            total_minutes: guestProfile.totalMinutes + sessionDurationMinutes,
            topics_covered: [...new Set([...guestProfile.topicsCovered, ...sessionTopics])],
            mira_color: miraOrbColor
          })
        });

        // Update local state with new accumulated values
        setGuestProfile(prev => ({
          ...prev,
          totalSessions: prev.totalSessions + 1,
          totalInteractions: prev.totalInteractions + sessionInteractionCount,
          totalMinutes: prev.totalMinutes + sessionDurationMinutes,
          topicsCovered: [...new Set([...prev.topicsCovered, ...sessionTopics])]
        }));
      } catch (error) {
        console.error('Error saving guest profile:', error);
      }

      // Show LearningSnapshot for investment visibility
      setShowLearningSnapshot(true);
    } else {
      showToastNotification('Session ended', 'info');
    }
  }, [isAuthenticated, isGuest, user, anonymousId, guestSessionCount, sessionStartTime, showToastNotification, guestProfile, sessionInteractionCount, sessionTopics, miraOrbColor]);

  // End session handler - extracted for reuse in mobile menu and control bars
  // Simple flow: Disconnect first, then show CollaborationCelebration with inline study buddy toggle
  const handleEndSession = async () => {
    haptics.sessionEnd();
    if (connectionState === 'idle') return;
    const wasConnected = connectionState === 'connected';
    const currentSessionId = sessionId; // Capture session ID before cleanup

    // CRITICAL: Capture room name BEFORE disconnect for study buddy matching
    // Both users share the same room, so roomName is the shared session identifier
    // This ensures mutual opt-in matching works (production-ready FERPA compliance)
    const roomInfo = voiceInputRef.current?.getRoomInfo?.();
    const sharedRoomName = roomInfo?.roomName || null;

    // FERPA-COMPLIANT: Fetch study partners from backend (server-side source of truth)
    // Uses Redis room_participants data - works even if frontend state was cleared
    let studyPartnerData = null;
    if (sharedRoomName && isAuthenticated) {
      try {
        const response = await axios.get(`/api/voice/session/participants/${sharedRoomName}`, {
          withCredentials: true
        });

        if (response.data.success && response.data.study_partners?.length > 0) {
          const firstPartner = response.data.study_partners[0];
          studyPartnerData = {
            user_id: firstPartner.user_id,
            name: firstPartner.name,
            avatar_initial: firstPartner.name?.charAt(0)?.toUpperCase() || '?'
          };
        }
      } catch (error) {
        // Graceful fallback to frontend state (less reliable but better than nothing)
        const firstFriend = friendsWhoStudied.length > 0 ? friendsWhoStudied[0] : null;
        if (firstFriend?.userId) {
          studyPartnerData = {
            user_id: firstFriend.userId,
            name: firstFriend.name,
            avatar_initial: firstFriend.name?.charAt(0)?.toUpperCase() || '?'
          };
        }
      }
    }

    // Simple flow: Disconnect and show celebration modal with inline study buddy toggle
    // Each user independently makes their choice, backend handles mutual matching
    await completeEndSession(sharedRoomName, currentSessionId, wasConnected, studyPartnerData);
  };

  // Start session with Mira and generate invite code
  const handleInviteFriend = async () => {
    // First, start the voice session if not already connected
    if (connectionState === 'idle') {
      setCurrentMode('voice');
      playActivationSound();
      setConnectionState('connecting');
      startTransition(() => {
        setIsCallActive(true);
        if (!voiceComponentInitialized) {
          setVoiceComponentInitialized(true);
        }
      });
    }

    // Generate the 4-digit study code
    try {
      const response = await fetch('/api/create-study-code', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user?.id || 'guest',
          student_name: user?.name || 'Student',
          subject: 'homework',
          problem_context: 'Study session'
        })
      });

      if (response.ok) {
        const data = await response.json();
        // Enable video collaboration panel
        setVideoCollaborationEnabled(true);

        // 2025 Study Code Sheet - Premium sharing experience
        setStudyCodeData({
          code: data.code,
          expiresAt: data.expires_at || new Date(Date.now() + 30 * 60 * 1000).toISOString(), // 30 min default
          joinUrl: `${window.location.origin}/join/${data.code}`
        });
        setShowStudyCodeSheet(true);
      }
    } catch (error) {
      console.error('Failed to generate study code:', error);
    }
  };

  const detectStruggle = (userInput, responseTime) => {
    const currentTime = Date.now();
    const timeSinceLastResponse = currentTime - lastResponseTime;
    
    // Frustration keywords that indicate struggle
    const frustrationKeywords = [
      'stuck', 'confused', 'don\'t understand', 'don\'t get', 'hard', 'difficult',
      'help', 'i\'m lost', 'what', 'how', 'why', 'can\'t figure', 'struggling'
    ];
    
    const containsFrustration = frustrationKeywords.some(keyword => 
      userInput.toLowerCase().includes(keyword.toLowerCase())
    );
    
    // Update struggle detection state
    setStruggleDetection(prev => {
      const newState = { ...prev };
      
      // Increment frustration keywords
      if (containsFrustration) {
        newState.frustrationKeywords += 1;
      }
      
      // Track time on current problem (if more than 5 minutes, it's struggle)
      if (timeSinceLastResponse > 300000) { // 5 minutes
        newState.timeOnCurrentProblem += 1;
      }
      
      // Determine if we should show help invitation
      newState.shouldShowHelp = (
        newState.frustrationKeywords >= 2 || 
        newState.consecutiveWrongAnswers >= 3 ||
        newState.timeOnCurrentProblem >= 1
      );
      
      return newState;
    });
    
    setLastResponseTime(currentTime);
    
    return containsFrustration;
  };
  
  const detectBreakthrough = (tutorResponse) => {
    // Keywords that indicate a breakthrough or achievement
    const breakthroughKeywords = [
      'excellent', 'great job', 'correct', 'well done', 'perfect',
      'you got it', 'exactly', 'that\'s right', 'breakthrough'
    ];
    
    return breakthroughKeywords.some(keyword => 
      tutorResponse.toLowerCase().includes(keyword.toLowerCase())
    );
  };
  

  // Check for guest help from friends
  const checkForGuestHelp = async () => {
    if (!user?.id || checkingForHelp) return;
    
    setCheckingForHelp(true);
    try {
      const response = await axios.get(`/api/guest-help/${user.id}/${sessionId}/check`);
      if (response.data.success && response.data.has_help) {
        // Add help directly as a chat message - ultra clean
        const helpMessage = {
          type: 'tutor',
          content: `🤝 ${response.data.help.helper_nickname || 'Your friend'} sent help: "${response.data.help.content}"`,
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, helpMessage]);
      }
    } catch (error) {
      console.error('Error checking for guest help:', error);
    } finally {
      setCheckingForHelp(false);
    }
  };
  
  // Handle feedback reactions for tutor messages
  const handleFeedbackSubmit = (messageId, reaction, comment = '') => {
    
    // Here you could implement an API call to store feedback in your backend
    // For now, we're just tracking it with PostHog
    try {
      posthog.capture('tutor_message_feedback', {
        messageId,
        reaction,
        comment,
        hasComment: Boolean(comment),
        sessionId,
        userTier,
        userId: user?.id
      });
      
      // If the feedback is negative and includes a comment, you might want to notify your team
      if (reaction === '😕' && comment) {
      }
    } catch (err) {
      console.error('PostHog tracking error:', err);
    }
  };
  
  // This function has been removed as part of the migration to the new visualization system
  
  /**
   * Request a visualization using the new visualization system
   * This is a simplified version that only uses the VisualizationController
   */
  const requestVisualization = async (prompt) => {
    try {
      
      // Use the onVisualizationRequest prop provided by VisualizationController
      if (onVisualizationRequest) {
        const visualizationData = {
          topic: prompt,
          context: messages.map(m => `${m.role}: ${m.content}`).join('\n'),
          timestamp: Date.now()
        };
        
        // Send request to the VisualizationController
        onVisualizationRequest(visualizationData);
        
        return visualizationData;
      }
      
      console.warn("No visualization controller available");
      return null;
    } catch (error) {
      console.error("Error requesting visualization:", error);
      return null;
    }
  };

  // Function to play activation sound using Web Audio API
  const playActivationSound = () => {
    // Create audio context
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    const audioCtx = new AudioContext();
    
    // Create oscillator for the swoosh sound
    const oscillator = audioCtx.createOscillator();
    const gainNode = audioCtx.createGain();
    
    // Create a filter for deeper sound
    const filter = audioCtx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 1200;
    filter.Q.value = 0.5;
    
    // Configure oscillator - deeper range (200-450Hz)
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(200, audioCtx.currentTime); // Start at 200Hz (deeper)
    oscillator.frequency.exponentialRampToValueAtTime(450, audioCtx.currentTime + 0.6); // Sweep to 450Hz
    
    // Configure volume envelope - stronger and more confident
    gainNode.gain.setValueAtTime(0, audioCtx.currentTime); // Start silent
    gainNode.gain.linearRampToValueAtTime(0.35, audioCtx.currentTime + 0.1); // Quicker, stronger attack
    gainNode.gain.linearRampToValueAtTime(0.2, audioCtx.currentTime + 0.6); // Sustained middle
    gainNode.gain.linearRampToValueAtTime(0, audioCtx.currentTime + 0.8); // Longer release
    
    // Connect and start
    oscillator.connect(filter);
    filter.connect(gainNode);
    gainNode.connect(audioCtx.destination);
    oscillator.start();
    oscillator.stop(audioCtx.currentTime + 0.8); // Slightly longer duration 800ms
  };

  // Auto-scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Guest anonymous ID initialization and PostHog tracking
  useEffect(() => {
    if (isGuest) {
      // Check URL parameters first (for sharing links with tracking)
      const params = new URLSearchParams(location.search);
      let anonId = params.get('anon');

      // If not in URL, check localStorage for existing ID
      if (!anonId) {
        anonId = localStorage.getItem('morpheus_anon_id');
      }

      // If still no anonymous ID, create one
      if (!anonId) {
        anonId = `anon-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
        localStorage.setItem('morpheus_anon_id', anonId);
      } else if (!localStorage.getItem('morpheus_anon_id')) {
        // Store in localStorage if it came from URL
        localStorage.setItem('morpheus_anon_id', anonId);
      }

      setAnonymousId(anonId);

      // Load saved Mira customization from localStorage
      const savedOrbColor = localStorage.getItem('mira_orb_color');
      if (savedOrbColor) {
        setMiraOrbColor(savedOrbColor);
      }

      // Load session count for conversion funnel
      const sessionCountKey = user
        ? `mira_session_count_free:${user.id}`
        : `mira_session_count_anon:${anonId}`;
      const savedSessionCount = parseInt(localStorage.getItem(sessionCountKey) || '0', 10);
      setGuestSessionCount(savedSessionCount);

      // Clean up URL parameter after storing
      if (params.get('anon')) {
        params.delete('anon');
        const newUrl = `${location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
        window.history.replaceState({}, document.title, newUrl);
      }

      // Identify the user in PostHog for persistent tracking across sessions
      try {
        posthog.identify(anonId);

        // Set initial properties in PostHog
        posthog.people.set({
          first_session_date: localStorage.getItem('morpheus_first_session_date') || new Date().toISOString(),
          user_type: 'guest',
          experience_type: 'investment_based'
        });
      } catch (e) {
        console.error('PostHog tracking error:', e);
      }
    }
  }, [isGuest, location, user]);

  // Load guest learning profile from backend for investment persistence
  useEffect(() => {
    const loadGuestProfile = async () => {
      if (!isGuest || !anonymousId || guestProfileLoaded) return;

      try {
        const response = await fetch(`/api/guest/learning-profile/${anonymousId}`, {
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.profile) {
            setGuestProfile({
              totalSessions: data.profile.total_sessions || 0,
              streakDays: data.profile.streak_days || 0,
              longestStreak: data.profile.longest_streak || 0,
              topicsCovered: data.profile.topics_covered || [],
              totalMinutes: data.profile.total_minutes || 0,
              totalInteractions: data.profile.total_interactions || 0,
              learningStyleWeights: data.profile.learning_style_weights || null,
              miraColor: data.profile.mira_color || null
            });
            // Apply saved Mira color if available
            if (data.profile.mira_color) {
              setMiraOrbColor(data.profile.mira_color);
            }
          }
        }
      } catch (error) {
        console.error('Error loading guest profile:', error);
      } finally {
        setGuestProfileLoaded(true);
      }
    };

    loadGuestProfile();
  }, [isGuest, anonymousId, guestProfileLoaded]);

  // Listen for Share with Parent events from FeatureGate
  useEffect(() => {
    const handleShareWithParent = async (event) => {
      const { tier } = event.detail;
      setShareTier(tier);
      setPaymentLoading(true);
      setPaymentError(null);

      try {
        // Generate 4-digit payment code via backend
        const response = await fetch('/api/create-payment-code', {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            anonymous_id: anonymousId,
            tier: tier,
            tokens_earned: tokensEarnedThisSession,
            session_interactions: sessionInteractionCount
          })
        });

        if (!response.ok) {
          throw new Error('Failed to generate payment code');
        }

        const data = await response.json();
        setShareCode(data.code); // 4-digit code like "4829"
        setShowShareWithParentModal(true);

        // Close toast when modal opens
        setShowToast(false);

        // Track code generation
        if (window.posthog) {
          posthog.capture('payment_code_generated', {
            code: data.code,
            tier: tier,
            anonymous_id: anonymousId,
            tokens_earned: tokensEarnedThisSession
          });
        }
      } catch (error) {
        console.error('Failed to generate payment code:', error);
        setPaymentError('Failed to generate code. Please try again.');
      } finally {
        setPaymentLoading(false);
      }
    };

    window.addEventListener('showShareWithParent', handleShareWithParent);
    return () => window.removeEventListener('showShareWithParent', handleShareWithParent);
  }, [anonymousId, tokensEarnedThisSession, sessionInteractionCount]);

  // Investment model: Trigger progress protection when guest is invested
  useEffect(() => {
    // Don't show again if user already dismissed it this session
    if (isGuest && !showProgressProtection && !showUpgradeModal && !progressProtectionDismissed) {
      // PHASE 3.2: Progress Protection Psychology Trigger
      // Show after significant investment accumulation (3+ interactions OR 50+ tokens)
      const hasSignificantInteractions = sessionInteractionCount >= 3;
      const hasSignificantTokens = tokensEarnedThisSession >= 50;

      if (hasSignificantInteractions || hasSignificantTokens) {
        // Delay to avoid interrupting active learning
        const timer = setTimeout(() => {
          setShowProgressProtection(true);

          // Track progress protection trigger in PostHog
          try {
            posthog.capture('progress_protection_triggered', {
              tokens_earned: tokensEarnedThisSession,
              interaction_count: sessionInteractionCount,
              trigger_reason: hasSignificantInteractions ? 'interaction_threshold' : 'token_threshold',
              session_type: 'guest',
              anonymous_id: anonymousId,
              experience_type: 'investment_based'
            });
          } catch (e) {
            console.error('PostHog tracking error:', e);
          }
        }, 3000); // 3 second delay for natural timing
        return () => clearTimeout(timer);
      }
    }
  }, [isGuest, sessionInteractionCount, tokensEarnedThisSession, showProgressProtection, showUpgradeModal, progressProtectionDismissed, anonymousId]);

  // Show CustomizeMiraPrompt at session 2 for anonymous guests (conversion funnel)
  // Creates emotional investment through personalization before they hit the paywall
  useEffect(() => {
    if (
      isAnonymous &&
      guestSessionCount === 1 &&
      miraOrbColor === 'default' &&
      !showCustomizeMiraPrompt &&
      connectionState === 'idle'
    ) {
      // Delay to not interrupt page load
      const timer = setTimeout(() => {
        setShowCustomizeMiraPrompt(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isAnonymous, guestSessionCount, miraOrbColor, showCustomizeMiraPrompt, connectionState]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // COMMENTED OUT: Guest help polling to eliminate re-renders during voice sessions
  // Check for guest help periodically
  // useEffect(() => {
  //   const checkInterval = setInterval(checkForGuestHelp, 10000); // Check every 10 seconds
  //   return () => clearInterval(checkInterval);
  // }, [user?.id, sessionId, checkingForHelp]);

  // Add initial tutor message when component mounts
  useEffect(() => {
    setMessages([]);
    
    // Add a starting milestone to guide the user
    addMilestone("Session started - Ask about any math concept to see it visualized!");
    
    // The visualization component is now managed by the VisualizationController
    // and will be displayed when the agent creates a visualization
    
    
    /* Previous code has been removed as part of the migration to the new visualization system
    setCanvasInstructions([
      // Main container - perfectly centered in the artifact panel
      { 
        type: 'rect', 
        x: centerX, 
        y: centerY, 
        width: 600, 
        height: 340, 
        fill: '#f8f9ff', 
        stroke: '#e0e4f8', 
        rx: 20, 
        ry: 20 
      },
      
      // Welcome header - centered at the top
      { 
        type: 'text', 
        content: 'Welcome to Tappy Tutor!', 
        x: centerX, 
        y: centerY - 130, 
        fontSize: 32, 
        color: '#4158D0', 
        align: 'center', 
        fontWeight: 'bold',
        textBaseline: 'middle'
      },
      { 
        type: 'text', 
        content: 'Making Math Visual & Interactive', 
        x: centerX, 
        y: centerY - 90, 
        fontSize: 20, 
        color: '#555555', 
        align: 'center',
        textBaseline: 'middle'
      },
      
      // Fraction examples caption
      { 
        type: 'text', 
        content: 'Ask me about math concepts like:', 
        x: centerX, 
        y: centerY - 50, 
        fontSize: 22, 
        color: '#333333', 
        align: 'center',
        textBaseline: 'middle'
      },
      
      // First circle - 1/2 example
      { 
        type: 'circle', 
        x: centerX - 150, 
        y: centerY + 20, 
        radius: 55, 
        stroke: '#4D5DDE', 
        fill: 'transparent', 
        strokeWidth: 2 
      },
      { 
        type: 'line', 
        startX: centerX - 150, 
        startY: centerY + 20 - 55, 
        endX: centerX - 150, 
        endY: centerY + 20 + 55, 
        color: '#4D5DDE', 
        width: 1.5 
      },
      { 
        type: 'shape', 
        shapeType: 'arc', 
        x: centerX - 150, 
        y: centerY + 20, 
        radius: 55, 
        startAngle: -Math.PI/2,          // Start from top
        endAngle: Math.PI/2,             // End at bottom (half circle)
        color: '#4D5DDE', 
        fill: 'rgba(77, 93, 222, 0.3)' 
      },
      { 
        type: 'text', 
        content: '1/2', 
        x: centerX - 150, 
        y: centerY + 95, 
        fontSize: 22, 
        color: '#4D5DDE', 
        align: 'center', 
        fontWeight: 'bold',
        textBaseline: 'middle'
      },
      
      // Second circle - 2/3 example
      { 
        type: 'circle', 
        x: centerX, 
        y: centerY + 20, 
        radius: 55, 
        stroke: '#4393E4', 
        fill: 'transparent', 
        strokeWidth: 2 
      },
      // Dividing lines for 2/3 (dividing circle into 3 equal parts)
      { 
        type: 'line', 
        startX: centerX, 
        startY: centerY + 20, 
        endX: centerX + (55 * Math.cos(Math.PI/3 + Math.PI/2)), 
        endY: centerY + 20 + (55 * Math.sin(Math.PI/3 + Math.PI/2)), 
        color: '#4393E4', 
        width: 1.5 
      },
      { 
        type: 'line', 
        startX: centerX, 
        startY: centerY + 20, 
        endX: centerX + (55 * Math.cos(Math.PI*5/3 + Math.PI/2)), 
        endY: centerY + 20 + (55 * Math.sin(Math.PI*5/3 + Math.PI/2)), 
        color: '#4393E4', 
        width: 1.5 
      },
      // Shaded area representing exactly 2/3 of the circle
      { 
        type: 'shape', 
        shapeType: 'arc', 
        x: centerX, 
        y: centerY + 20, 
        radius: 55, 
        startAngle: -Math.PI/2,              // Start from top
        endAngle: -Math.PI/2 + Math.PI*4/3,  // Cover 2/3 of the circle (240 degrees)
        color: '#4393E4', 
        fill: 'rgba(67, 147, 228, 0.3)' 
      },
      { 
        type: 'text', 
        content: '2/3', 
        x: centerX, 
        y: centerY + 95, 
        fontSize: 22, 
        color: '#4393E4', 
        align: 'center', 
        fontWeight: 'bold',
        textBaseline: 'middle'
      },
      
      // Third circle - 3/4 example
      { 
        type: 'circle', 
        x: centerX + 150, 
        y: centerY + 20, 
        radius: 55, 
        stroke: '#7747D5', 
        fill: 'transparent', 
        strokeWidth: 2 
      },
      // Dividing lines for 3/4 (dividing circle into 4 equal parts)
      { 
        type: 'line', 
        startX: centerX + 150, 
        startY: centerY + 20 - 55, 
        endX: centerX + 150, 
        endY: centerY + 20 + 55, 
        color: '#7747D5', 
        width: 1.5 
      },
      { 
        type: 'line', 
        startX: centerX + 150 - 55, 
        startY: centerY + 20, 
        endX: centerX + 150 + 55, 
        endY: centerY + 20, 
        color: '#7747D5', 
        width: 1.5 
      },
      // Shaded area representing exactly 3/4 of the circle
      { 
        type: 'shape', 
        shapeType: 'arc', 
        x: centerX + 150, 
        y: centerY + 20, 
        radius: 55, 
        startAngle: -Math.PI/2,              // Start from top
        endAngle: -Math.PI/2 + Math.PI*3/2,  // Cover 3/4 of the circle (270 degrees)
        color: '#7747D5', 
        fill: 'rgba(119, 71, 213, 0.3)' 
      },
      { 
        type: 'text', 
        content: '3/4', 
        x: centerX + 150, 
        y: centerY + 95, 
        fontSize: 22, 
        color: '#7747D5', 
        align: 'center', 
        fontWeight: 'bold',
        textBaseline: 'middle'
      },
      
      // Concept suggestion buttons
      { 
        type: 'rect', 
        x: centerX - 150, 
        y: centerY + 140, 
        width: 140, 
        height: 40, 
        fill: 'rgba(77, 93, 222, 0.1)', 
        stroke: 'rgba(77, 93, 222, 0.3)', 
        rx: 20, 
        ry: 20 
      },
      { 
        type: 'text', 
        content: 'Fractions', 
        x: centerX - 150, 
        y: centerY + 140, 
        fontSize: 18, 
        color: '#4D5DDE', 
        align: 'center',
        textBaseline: 'middle'
      },
      
      { 
        type: 'rect', 
        x: centerX, 
        y: centerY + 140, 
        width: 140, 
        height: 40, 
        fill: 'rgba(67, 147, 228, 0.1)', 
        stroke: 'rgba(67, 147, 228, 0.3)', 
        rx: 20, 
        ry: 20 
      },
      { 
        type: 'text', 
        content: 'Geometry', 
        x: centerX, 
        y: centerY + 140, 
        fontSize: 18, 
        color: '#4393E4', 
        align: 'center',
        textBaseline: 'middle'
      },
      
      { 
        type: 'rect', 
        x: centerX + 150, 
        y: centerY + 140, 
        width: 140, 
        height: 40, 
        fill: 'rgba(119, 71, 213, 0.1)', 
        stroke: 'rgba(119, 71, 213, 0.3)', 
        rx: 20, 
        ry: 20 
      },
      { 
        type: 'text', 
        content: 'Algebra', 
        x: centerX + 150, 
        y: centerY + 140, 
        fontSize: 18, 
        color: '#7747D5', 
        align: 'center',
        textBaseline: 'middle'
      }
    ]);
    */
  }, []);
  
  // Authentication is derived from user prop (see isAuthenticated const above)
  // No useEffect needed - App.js is the single source of truth
  
  // Reset breakthrough animation after it shows
  useEffect(() => {
    if (breakthroughDetected) {
      const timer = setTimeout(() => {
        setBreakthroughDetected(false);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [breakthroughDetected]);
  
  // Function to generate TTS for tutor responses - disabled for now
  const generateTTS = async (text) => {
    return;
  };

  // Detect learning style preferences from input
  const detectLearningStyle = (text) => {
    const textLower = text.toLowerCase();
    
    // Visual learning preferences
    const visualKeywords = ['show', 'see', 'look', 'diagram', 'picture', 'image', 'graph', 'visualize', 'draw'];
    
    // Auditory learning preferences
    const auditoryKeywords = ['tell', 'hear', 'listen', 'explain', 'discuss', 'talk', 'say', 'sound'];
    
    // Kinesthetic learning preferences
    const kinestheticKeywords = ['try', 'feel', 'do', 'practice', 'example', 'experience', 'handle', 'build'];
    
    // Reading/Writing learning preferences
    const readingKeywords = ['read', 'write', 'note', 'list', 'define', 'word', 'text', 'summarize'];
    
    // Count occurrences of keywords
    let visualCount = 0;
    let auditoryCount = 0;
    let kinestheticCount = 0;
    let readingCount = 0;
    
    visualKeywords.forEach(keyword => {
      if (textLower.includes(keyword)) visualCount++;
    });
    
    auditoryKeywords.forEach(keyword => {
      if (textLower.includes(keyword)) auditoryCount++;
    });
    
    kinestheticKeywords.forEach(keyword => {
      if (textLower.includes(keyword)) kinestheticCount++;
    });
    
    readingKeywords.forEach(keyword => {
      if (textLower.includes(keyword)) readingCount++;
    });
    
    // Update weights based on detected preferences
    const newWeights = { ...learningStyleWeights };
    
    if (visualCount > 0) newWeights.visual += 0.05 * visualCount;
    if (auditoryCount > 0) newWeights.auditory += 0.05 * auditoryCount;
    if (kinestheticCount > 0) newWeights.kinesthetic += 0.05 * kinestheticCount;
    if (readingCount > 0) newWeights['reading/writing'] += 0.05 * readingCount;
    
    // Normalize weights
    const total = Object.values(newWeights).reduce((sum, val) => sum + val, 0);
    
    Object.keys(newWeights).forEach(key => {
      newWeights[key] = newWeights[key] / total;
    });
    
    // Determine dominant style
    let maxWeight = 0;
    let dominantStyle = 'visual'; // Default
    
    Object.entries(newWeights).forEach(([style, weight]) => {
      if (weight > maxWeight) {
        maxWeight = weight;
        dominantStyle = style;
      }
    });
    
    // Only update if we detected something meaningful
    if (visualCount + auditoryCount + kinestheticCount + readingCount > 0) {
      setLearningStyleWeights(newWeights);
      setLearningStyle(dominantStyle);
    }
    
    return {
      style: dominantStyle,
      weights: newWeights
    };
  };

  // Event-driven friend participant tracking (prevents render loops)
  // Also handles real-time study buddy prompts via LiveKit data channel (FERPA-compliant)
  useEffect(() => {
    // Only register callbacks when voice component is initialized and call is active
    if (!voiceComponentInitialized || !isCallActive || !voiceInputRef.current?.registerFriendEvents) {
      // Reset friend count when not in active call
      setFriendParticipantsCount(0);
      return;
    }

    // Reset friend count and register event callbacks
    setFriendParticipantsCount(0);
    voiceInputRef.current.registerFriendEvents({
      onFriendJoined: (participant) => {
        setFriendParticipantsCount(prev => prev + 1);
        // Track friend info for study buddy feature (used by CollaborationCelebration)
        // Identity format: friend_{user_id}_{random} for auth users, friend_{random} for guests
        const identityParts = participant.identity?.split('_') || [];
        // For friend_61_abc123 -> ['friend', '61', 'abc123'] -> userId = '61'
        // For friend_abc123 -> ['friend', 'abc123'] -> userId = null (guest)
        const userId = identityParts.length >= 3 ? identityParts[1] : null;

        const friendData = {
          identity: participant.identity,
          name: participant.name || 'Study Buddy',
          userId: userId
        };

        // Add to current session list
        setFriendsInSession(prev => [...prev, friendData]);

        // Also track for study buddy prompt (persists even after they leave)
        // This ensures the host can still see study buddy prompt if friend leaves first
        if (userId) {
          setFriendsWhoStudied(prev => {
            // Don't add duplicates
            if (prev.some(f => f.userId === userId)) return prev;
            return [...prev, friendData];
          });
        }
      },
      onFriendLeft: (participant) => {
        setFriendParticipantsCount(prev => Math.max(0, prev - 1));
        setFriendsInSession(prev => prev.filter(f => f.identity !== participant.identity));
      }
    });

    // Cleanup callbacks on unmount
    return () => {
      if (voiceInputRef.current?.registerFriendEvents) {
        voiceInputRef.current.registerFriendEvents(null);
      }
      setFriendParticipantsCount(0);
      setFriendsInSession([]);
      setFriendsWhoStudied([]);
    };
  }, [voiceComponentInitialized, isCallActive]); // Only re-run when these change

  // Extract topics from user transcript for LearningSnapshot investment visibility
  const extractTopicsFromTranscript = useCallback((transcript) => {
    if (!transcript || transcript.length < 10) return [];

    const text = transcript.toLowerCase();
    const topics = [];

    // Subject detection patterns (common K-12 subjects)
    const subjectPatterns = {
      'Math': /\b(math|algebra|geometry|calculus|equation|fraction|decimal|percent|multiply|divide|add|subtract|quadratic|linear|slope|graph|variable|exponent|square root|pythagorean)\b/i,
      'Science': /\b(science|biology|chemistry|physics|cell|atom|molecule|gravity|force|energy|photosynthesis|ecosystem|evolution|hypothesis|experiment)\b/i,
      'English': /\b(english|essay|grammar|writing|reading|literature|poem|poetry|story|paragraph|thesis|vocabulary|sentence|punctuation|metaphor|simile)\b/i,
      'History': /\b(history|war|president|revolution|ancient|civilization|civil rights|world war|historical|constitution|amendment|colony|empire)\b/i,
      'Spanish': /\b(spanish|español|conjugat|verb tense|subjunctive|preterite|imperfect)\b/i,
      'Coding': /\b(code|coding|program|python|javascript|function|variable|loop|array|algorithm|debug)\b/i,
    };

    // More specific topic patterns
    const topicPatterns = {
      'Fractions': /\b(fraction|numerator|denominator|improper fraction|mixed number)\b/i,
      'Equations': /\b(equation|solve for|variable|equal|balance)\b/i,
      'Percentages': /\b(percent|percentage|discount|interest|tax)\b/i,
      'Essay Writing': /\b(essay|thesis|introduction|conclusion|paragraph|argument)\b/i,
      'Vocabulary': /\b(vocab|vocabulary|definition|word|meaning|synonym|antonym)\b/i,
      'Reading Comprehension': /\b(reading|comprehension|passage|main idea|inference|summary)\b/i,
    };

    // Check subject patterns first
    for (const [subject, pattern] of Object.entries(subjectPatterns)) {
      if (pattern.test(text)) {
        topics.push(subject);
        break; // Only add one main subject
      }
    }

    // Check for specific topics
    for (const [topic, pattern] of Object.entries(topicPatterns)) {
      if (pattern.test(text) && !topics.includes(topic)) {
        topics.push(topic);
        if (topics.length >= 3) break; // Max 3 topics
      }
    }

    return topics;
  }, []);

  // 2026 Curiosity Detection - Frontend heuristics for instant ambient feedback
  // No latency - runs client-side on each transcript
  // Returns 0.0-1.0 curiosity signal for this utterance
  const detectCuriositySignal = useCallback((transcript) => {
    if (!transcript || transcript.length < 5) return 0;

    const lower = transcript.toLowerCase().trim();
    let signal = 0;

    // Curiosity keywords (why, what if, how come, I wonder)
    const curiosityKeywords = [
      'why', 'what if', 'how come', 'i wonder', 'what would happen',
      'tell me more', 'that\'s interesting', 'that\'s cool', 'wow',
      'really', 'no way', 'how does', 'what makes', 'could we',
      'is it possible', 'what about', 'but what if'
    ];

    for (const keyword of curiosityKeywords) {
      if (lower.includes(keyword)) {
        signal += 0.25;
        break; // Only count once per category
      }
    }

    // Question indicators
    if (lower.endsWith('?') || lower.includes('?')) {
      signal += 0.15;
    }

    // Longer questions = more exploration (not quick factual queries)
    if (transcript.length > 60) {
      signal += 0.2;
    } else if (transcript.length > 30) {
      signal += 0.1;
    }

    // Follow-up indicators
    const followUpPhrases = ['and also', 'another thing', 'one more', 'wait but', 'oh and'];
    for (const phrase of followUpPhrases) {
      if (lower.includes(phrase)) {
        signal += 0.15;
        break;
      }
    }

    return Math.min(1.0, signal);
  }, []);

  // Update curiosity momentum with EMA smoothing (smooth transitions, never jumps)
  const updateCuriosityMomentum = useCallback((signal) => {
    const now = Date.now();
    const timeSinceLastUpdate = now - lastCuriosityUpdateRef.current;

    // Faster decay if no signal, slower rise for sustained curiosity
    const alpha = signal > 0.3 ? 0.25 : 0.15; // Rise slowly, decay faster

    setCuriosityMomentum(prev => {
      const newMomentum = alpha * signal + (1 - alpha) * prev;
      // Clamp to 0.1-0.95 (never fully dead or maxed out)
      return Math.max(0.1, Math.min(0.95, newMomentum));
    });

    lastCuriosityUpdateRef.current = now;
  }, []);

  // Handle direct voice transcript input from VoiceInput component
  const handleVoiceTranscript = (transcript) => {
    // Update the input field with transcript
    setInput(transcript);

    // 2026: Detect curiosity signal and update momentum (ambient feedback)
    const curiositySignal = detectCuriositySignal(transcript);
    if (curiositySignal > 0) {
      updateCuriosityMomentum(curiositySignal);
    }

    // Extract topics for LearningSnapshot (conversion funnel investment visibility)
    const newTopics = extractTopicsFromTranscript(transcript);
    if (newTopics.length > 0) {
      setSessionTopics(prev => {
        const combined = [...new Set([...prev, ...newTopics])];
        return combined.slice(0, 5); // Keep max 5 topics
      });
    }

    // Update cognitive state based on interaction patterns
    const newCognitiveState = sessionInteractionCount > 5 ? 'confident'
                            : sessionInteractionCount > 2 ? 'progressing'
                            : 'building_confidence';
    setSessionCognitiveState(newCognitiveState);

    // Automatically submit the transcript after a short delay
    // This allows user to see what was transcribed before sending
    setTimeout(() => {
      if (transcript.trim()) {
        handleSubmit(new Event('submit'));
      }
    }, 800);
  };
  
  // New function to handle visualization requests from our VisualizationSystem
  // This is passed down from VisualizationController and used by LiveKitVoiceInput
  const handleVisualizationRequest = (data) => {
    // If we received onVisualizationRequest, use it
    if (onVisualizationRequest) {
      onVisualizationRequest(data);
      
      // Also set the last visualization source for session analytics
      setLastVisualizationSource('voice');
    } else {
      console.warn('Visualization request received but no handler is provided');
    }
  };
  
  // Handle sound effect triggers from reading companion
  const handleSoundEffects = (soundData) => {
    if (!soundsReady) {
      console.warn('Sound effects not ready yet');
      return;
    }
    
    try {
      if (soundData.type === 'reading_sound_effects') {
        
        soundData.triggers.forEach(trigger => {
          if (soundManager) {
            soundManager.playSound(trigger.sound, trigger.options);
          } else {
            console.warn('🎵 SoundManager not available');
          }
        });
      } else if (soundData.type === 'sound_control' && soundData.action === 'stop_all') {
        stopAllSounds();
      }
    } catch (error) {
      console.error('Error triggering sound effects:', error);
    }
  };

  // Platform-specific instant sharing for maximum virality
  const generateShareLink = async () => {
    try {
      
      // Get current room info from voice component
      const roomInfo = voiceInputRef.current?.getRoomInfo?.();
      
      if (!roomInfo || !roomInfo.roomName) {
        console.warn('No active voice session to share');
        showToastNotification('Start a voice session with Mira first, then you can invite friends to join! 🎤', 'warning');
        return;
      }
      
      // Generate secure invitation token for specific friend
      const friendName = prompt("Who are you inviting? (Enter their name)");
      if (!friendName || !friendName.trim()) {
        return; // User cancelled or didn't enter name
      }
      
      // Generate secure invitation with friend's name
      const inviteResponse = await fetch('/api/voice/create-invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          room_name: roomInfo.roomName,
          friend_name: friendName.trim(),
          session_id: sessionId
        })
      });
      
      if (!inviteResponse.ok) {
        showToastNotification('Failed to create invitation. Please try again.', 'error');
        return;
      }
      
      const inviteData = await inviteResponse.json();
      const shareableUrl = `${window.location.origin}/join/${inviteData.invite_token}`;
      
      // Phase 2: Create token escrow for viral conversion incentive
      try {
        const escrowResponse = await fetch('/api/tokens/escrow/create', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            inviter_id: username || 'anonymous',
            friend_name: friendName.trim(),
            escrow_amount: 50
          })
        });
        
        if (escrowResponse.ok) {
          const escrowData = await escrowResponse.json();
          
          // Show user the escrow is active
          setAchievement({
            title: '💰 Token Escrow Active!',
            description: `50 tokens escrowed for ${friendName}. You'll both get bonus tokens if they upgrade!`,
            points: 0
          });
          setTimeout(() => setAchievement(null), 4000);
          
          // Don't trigger token animation since no immediate tokens are earned
          // The escrow system holds tokens until friend upgrades
        }
      } catch (error) {
        // Token escrow failed (non-critical)
      }
      
      const studentMessage = `${friendName}, join my study session! 📚✨ (Plus we both get token bonuses! 🪙)`;
      
      
      // Platform detection and direct deep linking
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobile = /iphone|ipad|android|mobile/.test(userAgent);
      const isIOS = /iphone|ipad/.test(userAgent);
      const isAndroid = /android/.test(userAgent);
      
      if (isMobile) {
        // Try platform-specific sharing for instant app opening
        await tryPlatformSpecificSharing(shareableUrl, studentMessage, isIOS, isAndroid);
      } else {
        // Desktop: Smart platform detection and direct links
        await tryDesktopPlatformSharing(shareableUrl, studentMessage);
      }
      
      // Track sharing event
      if (typeof posthog !== 'undefined') {
        posthog.capture('session_shared', {
          session_id: sessionId,
          room_name: roomInfo.roomName,
          user_tier: userTier,
          platform: isMobile ? (isIOS ? 'ios' : 'android') : 'desktop'
        });
      }
      
    } catch (error) {
      console.error('Error generating share link:', error);
      // Simple error handling
      const button = document.querySelector('.share-button');
      if (button) {
        const originalHTML = button.innerHTML;
        button.innerHTML = '<span style="color: #ef4444;">Try again</span>';
        setTimeout(() => {
          button.innerHTML = originalHTML;
        }, 2000);
      }
    }
  };

  // Platform-specific sharing for mobile devices
  const tryPlatformSpecificSharing = async (shareableUrl, message, isIOS, isAndroid) => {
    const encodedMessage = encodeURIComponent(message);
    const encodedUrl = encodeURIComponent(shareableUrl);
    const fullMessage = encodeURIComponent(`${message} ${shareableUrl}`);
    
    // Try native Web Share API first (opens native share sheet)
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Study together! 📚',
          text: message,
          url: shareableUrl
        });
        showShareSuccess();
        return;
      } catch (err) {
      }
    }
    
    // Platform-specific deep links as fallback
    if (isIOS) {
      // iOS specific apps - these will auto-open if installed
      try {
        // Try iMessage first (most common)
        window.location.href = `sms:&body=${fullMessage}`;
        showShareSuccess('Opening Messages...');
        return;
      } catch (err) {
      }
    }
    
    if (isAndroid) {
      // Android specific apps
      try {
        // Try Android Messages first
        const intent = `intent:#Intent;action=android.intent.action.SEND;type=text/plain;S.android.intent.extra.TEXT=${fullMessage};end`;
        window.location.href = intent;
        showShareSuccess('Opening Messages...');
        return;
      } catch (err) {
      }
    }
    
    // Universal fallback - copy to clipboard
    await navigator.clipboard.writeText(`${message} ${shareableUrl}`);
    showShareSuccess('Copied! Paste anywhere');
  };

  // Desktop platform sharing with smart detection
  const tryDesktopPlatformSharing = async (shareableUrl, message) => {
    const encodedMessage = encodeURIComponent(message);
    const encodedUrl = encodeURIComponent(shareableUrl);
    const fullMessage = encodeURIComponent(`${message} ${shareableUrl}`);
    
    // Create quick-access sharing options
    const showQuickShareOptions = () => {
      const shareOptions = document.createElement('div');
      shareOptions.setAttribute('data-share-modal', 'true');
      shareOptions.style.cssText = `
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: white;
        border-radius: 12px;
        box-shadow: 0 8px 32px rgba(0,0,0,0.2);
        padding: 20px;
        z-index: 10000;
        font-family: 'Poppins', sans-serif;
      `;
      
      shareOptions.innerHTML = `
        <div style="text-align: center; margin-bottom: 16px;">
          <h3 style="margin: 0; color: #297a92;">Share Study Session</h3>
        </div>
        <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px; margin-bottom: 12px;">
          <button onclick="window.open('https://discord.com/channels/@me?text=${fullMessage}', '_blank'); document.querySelector('[data-share-modal]').remove();" 
                  style="padding: 12px; border: none; border-radius: 8px; background: #5865F2; color: white; cursor: pointer; font-weight: 500; font-size: 14px;">
            Discord 💬
          </button>
          <button onclick="window.open('https://wa.me/?text=${fullMessage}', '_blank'); document.querySelector('[data-share-modal]').remove();" 
                  style="padding: 12px; border: none; border-radius: 8px; background: #25D366; color: white; cursor: pointer; font-weight: 500; font-size: 14px;">
            WhatsApp 📱
          </button>
          <button onclick="window.location.href='sms:?body=${fullMessage}'; this.innerHTML='Opening...'; setTimeout(() => document.querySelector('[data-share-modal]').remove(), 1000);" 
                  style="padding: 12px; border: none; border-radius: 8px; background: #297a92; color: white; cursor: pointer; font-weight: 500; font-size: 14px;">
            Text 💬
          </button>
        </div>
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px;">
          <button onclick="navigator.clipboard.writeText('${message} ${shareableUrl}'); this.innerHTML='✓ Copied!'; setTimeout(() => document.querySelector('[data-share-modal]').remove(), 1500);" 
                  style="padding: 10px; border: 1px solid #d1d5db; border-radius: 6px; background: white; color: #374151; cursor: pointer; font-weight: 500; font-size: 13px;">
            📋 Copy Link
          </button>
          <button onclick="document.querySelector('[data-share-modal]').remove();" 
                  style="padding: 10px; border: 1px solid #d1d5db; border-radius: 6px; background: #f9fafb; color: #6b7280; cursor: pointer; font-weight: 500; font-size: 13px;">
            Cancel
          </button>
        </div>
      `;
      
      document.body.appendChild(shareOptions);
      
      // Auto-remove after 10 seconds
      setTimeout(() => {
        const modal = document.querySelector('[data-share-modal]');
        if (modal) {
          modal.remove();
        }
      }, 10000);
    };
    
    showQuickShareOptions();
  };

  // Show success feedback
  const showShareSuccess = (customMessage = '✓ Shared!') => {
    const button = document.querySelector('.share-button');
    if (button) {
      const originalHTML = button.innerHTML;
      button.innerHTML = `<span style="color: #22c55e;">${customMessage}</span>`;
      setTimeout(() => {
        button.innerHTML = originalHTML;
      }, 2000);
    }
  };

  // Check for optimal Neo setup trigger moment (engagement-optimized) - DISABLED
  const checkNeoSetupTrigger = (tutorResponse) => {
    // Feature disabled - Neo setup removed
    return;
  };

  // Handle Neo setup completion
  const handleNeoSetupComplete = (neoData) => {
    // Feature disabled - Neo setup removed
    return;
    /* Disabled code:
    setTokensEarnedThisSession(prev => prev + neoData.tokensEarned);
    setTokenBalance(prev => prev + neoData.tokensEarned);

    // Show achievement for completing Neo setup
    setAchievement({
      title: `🎭 ${neoData.neoName} is Ready!`,
      description: `Your personalized AI tutor earned you ${neoData.tokensEarned} #EDU tokens`,
      points: neoData.tokensEarned
    });
    setTimeout(() => setAchievement(null), 4000);

    // Update user object to reflect the new Neo name
    if (user) {
      user.neo_name = neoData.neoName;
      user.neo_personality = neoData.neoPersonality;
    }
    */
  };

  // Multi-friend invitation function (FaceTime pattern)
  const generateInviteMoreFriends = async () => {
    try {
      
      // Get current room info and check friend participants count
      const roomInfo = voiceInputRef.current?.getRoomInfo?.();
      
      if (!roomInfo || !roomInfo.roomName) {
        console.warn('No active voice session to share');
        showToastNotification('Start a voice session with Mira first, then you can invite more friends! 🎤', 'warning');
        return;
      }

      if (friendParticipantsCount === 0) {
        console.warn('No existing friends in session - use regular share instead');
        showToastNotification('Use the regular share button to invite your first friend! Then you can invite more friends. 🌊', 'info');
        return;
      }

      // Security check: Only authenticated users can invite additional friends
      if (!user || !user.id) {
        console.warn('Authentication required for multi-friend invitations');
        showToastNotification('Please sign up to invite additional friends to your study session! 🔐', 'warning');
        return;
      }
      
      // Generate secure invitation for additional friend
      const friendName = prompt("Who else are you inviting? (Enter their name)");
      if (!friendName || !friendName.trim()) {
        return; // User cancelled or didn't enter name
      }
      
      // Call the multi-friend API endpoint
      const inviteResponse = await fetch(`/api/voice/invite-additional-friend/${roomInfo.roomName}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          friend_name: friendName.trim(),
          session_id: sessionId
        })
      });
      
      if (!inviteResponse.ok) {
        const errorData = await inviteResponse.json();
        showToastNotification(`Failed to create invitation: ${errorData.error || 'Please try again.'}`, 'error');
        return;
      }
      
      const inviteData = await inviteResponse.json();
      const shareableUrl = `${window.location.origin}/join/${inviteData.invite_token}`;
      
      // Phase 2: Create token escrow for viral conversion incentive
      try {
        const escrowResponse = await fetch('/api/tokens/escrow/create', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            inviter_id: username,
            friend_name: friendName.trim(),
            escrow_amount: 75 // Higher reward for multi-friend invitations
          })
        });
        
        if (escrowResponse.ok) {
          const escrowData = await escrowResponse.json();
          
          // Show user the escrow is active
          setAchievement({
            title: '🌊 Group Study Bonus!',
            description: `75 tokens escrowed for ${friendName}. Building the ultimate study group! 🎉`,
            points: 0
          });
          setTimeout(() => setAchievement(null), 4000);
          
          // Don't trigger token animation since no immediate tokens are earned
          // The escrow system holds tokens until friend upgrades
        }
      } catch (error) {
        // Token escrow failed (non-critical)
      }
      
      const studentMessage = `${friendName}, join our group study session! 📚✨ (Everyone gets bonus tokens! 🪙)`;
      
      
      // Platform detection and direct deep linking
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobile = /iphone|ipad|android|mobile/.test(userAgent);
      const isIOS = /iphone|ipad/.test(userAgent);
      const isAndroid = /android/.test(userAgent);
      
      if (isMobile) {
        // Try platform-specific sharing for instant app opening
        await tryPlatformSpecificSharing(shareableUrl, studentMessage, isIOS, isAndroid);
      } else {
        // Desktop: Smart platform detection and direct links
        await tryDesktopPlatformSharing(shareableUrl, studentMessage);
      }
      
      // Track multi-friend sharing event
      if (typeof posthog !== 'undefined') {
        posthog.capture('multi_friend_invite_created', {
          session_id: sessionId,
          room_name: roomInfo.roomName,
          user_tier: userTier,
          existing_friends: friendParticipantsCount,
          total_chain_position: inviteData.chain_position,
          platform: isMobile ? (isIOS ? 'ios' : 'android') : 'desktop'
        });
      }
      
    } catch (error) {
      console.error('Error generating additional friend invitation:', error);
      
      // Simple error handling
      const button = document.querySelector('.invite-more-button');
      if (button) {
        const originalHTML = button.innerHTML;
        button.innerHTML = '<span style="color: #ef4444;">Try again</span>';
        setTimeout(() => {
          button.innerHTML = originalHTML;
        }, 2000);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!input.trim()) return;
    
    const userMessage = {
      role: 'student',
      content: input
    };
    
    // Detect learning style from input
    const detectedStyle = detectLearningStyle(input);
    
    // If input contains voice-related keywords, highlight that feature
    const voiceKeywords = ['speak', 'talk', 'listen', 'voice', 'verbal', 'hear', 'audio'];
    const requestsVoice = voiceKeywords.some(keyword => input.toLowerCase().includes(keyword));
    
    // Check if this is a higher-order thinking request (analyze, evaluate, create)
    // Only premium+ users can access these taxonomy levels
    const analyzeKeywords = ['analyze', 'analysis', 'examine', 'investigate', 'compare'];
    const evaluateKeywords = ['evaluate', 'assess', 'judge', 'critique', 'recommend', 'determine value'];
    const createKeywords = ['create', 'design', 'construct', 'compose', 'develop', 'formulate'];
    
    const isAnalyzeRequest = analyzeKeywords.some(keyword => input.toLowerCase().includes(keyword));
    const isEvaluateRequest = evaluateKeywords.some(keyword => input.toLowerCase().includes(keyword));
    const isCreateRequest = createKeywords.some(keyword => input.toLowerCase().includes(keyword));
    const isHigherOrderRequest = isAnalyzeRequest || isEvaluateRequest || isCreateRequest;
    
    // Determine if the request is for an advanced feature
    const needsAdvancedFeature = userTier === 'student' && isHigherOrderRequest;
    const featureRequired = isAnalyzeRequest ? 'TAXONOMY_ANALYZE' : 
                           isEvaluateRequest ? 'TAXONOMY_EVALUATE' : 
                           isCreateRequest ? 'TAXONOMY_CREATE' : null;
    
    if (needsAdvancedFeature && featureRequired) {
      // Show a premium-only message for higher-order thinking questions
      const systemMessage = {
        role: 'system',
        content: `Note: Your question appears to involve ${featureRequired.toLowerCase().replace('taxonomy_', '')} level thinking.`
      };
      
      const upgradeMessage = {
        role: 'tutor',
        content: `I can help with this, but I'll modify my response to focus on remember, understand, and apply levels rather than ${featureRequired.toLowerCase().replace('taxonomy_', '')}. Premium users get access to all taxonomy levels. Would you like me to continue with a modified response, or would you prefer to upgrade to Premium?`
      };
      
      setMessages(prev => [...prev, userMessage, systemMessage, upgradeMessage]);
      setInput('');
      setLoading(false);
      
      // Add milestone for taxonomy level request
      addMilestone(`Higher-order thinking (${featureRequired.toLowerCase().replace('taxonomy_', '')}) request identified - adjusting response`);
      
      return; // Skip the regular API call
    }
    
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    
    try {
      // Use visualization API for text input
      const response = await axios.post('/api/tutor/visualization', {
        query: input,
        session_id: sessionId,
        use_tts: detectedStyle.style === 'auditory' || requestsVoice,
        user_tier: userTier
      });
      
      
      // Create tutor message from the response
      const tutorMessage = {
        role: 'tutor',
        content: response.data.response || "Here's a visualization that might help you understand this concept."
      };
      
      // If the API doesn't respond with has_canvas, default to true for testing
      if (response.data.has_canvas === undefined) {
        response.data.has_canvas = true;
      }
      
      setMessages(prev => [...prev, tutorMessage]);
      
      // Update learning style from response if available
      if (response.data.learning_style) {
        setLearningStyle(response.data.learning_style);
      }
      
      // Track interactions for Neo setup trigger
      setSessionInteractionCount(prev => prev + 1);
      
      // Trigger Neo setup at optimal engagement moment
      checkNeoSetupTrigger(tutorMessage.content);
      
      // TTS generation is disabled
      // if (response.data.response) {
      //   generateTTS(response.data.response);
      // }
      
      // Check if user is asking for a visualization that should use the artifacts system
      // Keywords that might indicate a request for a specialized visualization
      const artifactKeywords = [
        'show me a diagram', 'create a visual', 'generate a diagram', 
        'visualize', 'show a diagram', 'create diagram', 'artifact'
      ];
      
      const shouldUseArtifacts = artifactKeywords.some(keyword => 
        input.toLowerCase().includes(keyword.toLowerCase())
      );
      
      // If we should use the visualization system, make a request
      if (shouldUseArtifacts) {
        // Request a visualization in parallel - this won't block the main response
        requestVisualization(input).then(artifactData => {
          if (artifactData) {
          }
        });
      }
      
      // Check if we should show visualization
      
      // Track that this visualization came from text input
      setLastVisualizationSource('text');
      
      // Extract steps from the response
      const steps = response.data.visualization_steps || response.data.canvas_instructions;
      
      // Check if this is a React component visualization
      if (response.data.has_canvas && response.data.visualization_type === 'react' && response.data.react_component) {
        
        // Check if this is a premium visualization (contains neural network or advanced features)
        const isPremiumComponent = 
          response.data.react_component.includes('NeuralNetwork') || 
          response.data.react_component.includes('premium') ||
          response.data.is_advanced === true;
        
        // For student tier, if this is premium content, flag it
        if (userTier === 'student' && isPremiumComponent) {
          // For truly advanced visualizations, we may want to restrict or show a simpler version
          // but for demonstration purposes, we'll just add a premium flag to show the badge
          // We could also modify the component code to remove premium features
          
          if (response.data.react_component.includes('premium')) {
            // Add a system message noting the premium features
            setMessages(prev => [...prev, {
              role: 'system',
              content: 'Note: This visualization contains premium features. Premium users get enhanced visualizations.'
            }]);
          }
        }
        
        // Update UI
        setShowStepByStep(false); // React components handle their own steps
        setShowArtifactPanel(true);
        setActiveArtifactTab('visualization');
        
        // Set React component with delay to ensure state update
        setTimeout(() => {
          setReactComponent(response.data.react_component);
        }, 100);
        
        // Reset canvas instructions to avoid conflicts
        setCanvasInstructions(null);
        
        // Check for breakthrough
        if (response.data.isBreakthrough) {
          setBreakthroughDetected(true);
          addMilestone(response.data.breakthroughMessage || "You had a breakthrough in understanding!");
        } else {
          // Add a generic milestone for any visualization
          addMilestone("Interactive visualization created to help understand this concept");
        }
      }
      // Check if visualization should be shown (legacy JSON format)
      else if (response.data.has_canvas && steps && steps.length > 0) {
        
        // Check if there are any premium steps in the visualization
        const hasPremiumSteps = steps.some(step => 
          step.isPremium === true || 
          step.premium === true || 
          (step.drawInstructions && step.drawInstructions.some(instr => 
            instr.premium === true || 
            (instr.type === '3d' && userTier === 'student')
          ))
        );
        
        // For student tier, if there are premium steps, we may need to simplify
        if (userTier === 'student' && hasPremiumSteps) {
          // Mark premium features for visual indicators
          steps.forEach(step => {
            if (step.isPremium === true || step.premium === true) {
              step.isPremium = true; // Ensure isPremium flag is set for tier badge
            }
            
            // Also check draw instructions for premium features
            if (step.drawInstructions) {
              step.drawInstructions.forEach(instr => {
                if (instr.premium === true || instr.type === '3d') {
                  step.isPremium = true; // Mark parent step as premium
                }
              });
            }
          });
          
          // Add a system message about premium visualizations
          if (hasPremiumSteps) {
            setMessages(prev => [...prev, {
              role: 'system',
              content: 'Note: Some advanced visualization features are available with Premium accounts.'
            }]);
          }
        }
        
        // Update UI
        setShowStepByStep(true);
        setShowArtifactPanel(true);
        setActiveArtifactTab('visualization');
        
        // Set canvas instructions
        setCanvasInstructions(steps);
        
        // Reset React component to avoid conflicts
        setReactComponent(null);
        
        // Check for breakthrough
        const breakthroughStep = steps.find(step => step.isBreakthrough);
        if (breakthroughStep) {
          setBreakthroughDetected(true);
          addMilestone(breakthroughStep.breakthroughMessage || "You had a breakthrough in understanding!");
        } else {
          // Add a generic milestone for any visualization
          addMilestone("New visualization to help understand this concept");
        }
      } else {
        // Create a basic fallback visualization
        const defaultInstructions = [
          {
            title: "Math Visualization",
            formula: "f(x) = 2x + 1",
            explanation: "Here's a simple math visualization to help you understand the concept.",
            drawInstructions: [
              {"type": "text", "text": "Math Visualization", "x": 250, "y": 30, "fontSize": 24, "color": "#4158D0"},
              {"type": "line", "x1": 50, "y1": 100, "x2": 450, "y2": 100, "color": "#000000"},  // x-axis
              {"type": "line", "x1": 50, "y1": 50, "x2": 50, "y2": 150, "color": "#000000"},    // y-axis
              {"type": "text", "text": "x", "x": 450, "y": 120, "fontSize": 16},
              {"type": "text", "text": "y", "x": 30, "y": 50, "fontSize": 16}
            ]
          }
        ];
        
        // Show the fallback visualization
        setCanvasInstructions(defaultInstructions);
        setShowStepByStep(true);
        setShowArtifactPanel(true);
        setActiveArtifactTab('visualization');
      }
      
      // If there are breakthrough messages, show them
      if (response.data.breakthrough_messages && response.data.breakthrough_messages.length > 0) {
        setBreakthroughDetected(true);
        // Automatically show the artifact panel when breakthrough messages are received
        setShowArtifactPanel(true);
      }
      
      // Simulate student state changes based on the interaction
      setStudentState(prevState => {
        const newState = [...prevState];
        // Increment knowledge by a small amount
        newState[0] = Math.min(100, newState[0] + 2);  
        // Slightly decrease confusion
        newState[2] = Math.max(0, newState[2] - 1);
        return newState;
      });
      
      // If the user used voice input, boost auditory learning style a bit more
      if (requestsVoice) {
        setLearningStyleWeights(prev => {
          const updated = {...prev};
          updated.auditory = Math.min(1, updated.auditory + 0.1);
          
          // Normalize weights
          const total = Object.values(updated).reduce((sum, val) => sum + val, 0);
          Object.keys(updated).forEach(key => {
            updated[key] = updated[key] / total;
          });
          
          return updated;
        });
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error sending message:', error);
      console.error('Error details:', error.response ? error.response.data : 'No response data');
      
      // Attempt to show a helpful error message
      let errorMsg = "Sorry, there was an error communicating with the tutor.";
      
      if (error.response) {
        // Server responded with an error
        errorMsg += ` Server error: ${error.response.status}`;
        if (error.response.data && error.response.data.error) {
          errorMsg += ` - ${error.response.data.error}`;
        }
      } else if (error.request) {
        // Request made but no response received
        errorMsg += " No response from server. Please check your connection.";
      } else {
        // Error in request setup
        errorMsg += ` ${error.message}`;
      }
      
      const errorMessage = {
        role: 'system',
        content: errorMsg
      };
      
      setMessages(prev => [...prev, errorMessage]);
      setLoading(false);
      
      // Show a temporary testing message with instructions instead of failing completely
      setTimeout(() => {
        const helpMessage = {
          role: 'tutor',
          content: "While we're fixing the connection issues, let me provide some help anyway. Try asking about a math concept like 'quadratic equations' or 'Pythagorean theorem'"
        };
        setMessages(prev => [...prev, helpMessage]);
        
        // Create some example visualization instructions for testing
        const testInstructions = [
          {
            title: "Quadratic Formula",
            formula: "x = \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}",
            explanation: "The quadratic formula gives us the solutions to any quadratic equation: $x = \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}$",
            drawInstructions: [
              {"type": "text", "text": "Quadratic Formula:", "x": 20, "y": 30, "font": "16px Arial", "color": "#333"},
              {"type": "line", "startX": 80, "startY": 70, "endX": 320, "endY": 70},
              {"type": "text", "text": "-b ± √(b² - 4ac)", "x": 140, "y": 60, "font": "16px Arial", "color": "#000"},
              {"type": "text", "text": "2a", "x": 180, "y": 90, "font": "16px Arial", "color": "#000"}
            ],
            isBreakthrough: true,
            breakthroughMessage: "The quadratic formula is the key to solving any quadratic equation."
          }
        ];
        
        setCanvasInstructions(testInstructions);
        setShowStepByStep(true);
        setShowArtifactPanel(true);
        setActiveArtifactTab('visualization');
      }, 1000);
    }
  };

  // Function to format formulas in text with KaTeX
  const formatTextWithFormulas = (text) => {
    if (!text) return '';
    
    // Regular expression to identify LaTeX formulas between $ $ or $$ $$ delimiters
    const formula_regex = /\$\$([\s\S]*?)\$\$|\$([\s\S]*?)\$/g;
    
    // Split text into parts (text and formula)
    const parts = [];
    let lastIndex = 0;
    let match;
    
    while ((match = formula_regex.exec(text)) !== null) {
      // Add text before the formula
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: text.substring(lastIndex, match.index)
        });
      }
      
      // Add the formula
      const formula = match[1] || match[2];
      parts.push({
        type: 'formula',
        content: formula,
        isBlock: !!match[1] // $$ $$ is block, $ $ is inline
      });
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < text.length) {
      parts.push({
        type: 'text',
        content: text.substring(lastIndex)
      });
    }
    
    // Render parts
    return parts.map((part, index) => {
      if (part.type === 'text') {
        return <span key={index}>{part.content}</span>;
      } else if (part.type === 'formula') {
        return (
          <span key={index} className="formula">
            <BlockMath math={part.content} />
          </span>
        );
      }
      return null;
    });
  };

  // This function has been replaced by the toggleArtifactPanel function

  // Get learning style color with high contrast option
  const getLearningStyleColor = (style, highContrast = false) => {
    if (highContrast) {
      // Higher contrast colors for accessibility
      switch (style) {
        case 'visual': return '#00A000'; // High contrast green
        case 'auditory': return '#0050C0'; // High contrast blue 
        case 'kinesthetic': return '#D97700'; // High contrast orange
        case 'reading/writing': return '#8000B0'; // High contrast purple
        default: return '#505050'; // High contrast gray
      }
    } else {
      // Standard colors
      switch (style) {
        case 'visual': return '#4ade80'; // Green
        case 'auditory': return '#3b82f6'; // Blue
        case 'kinesthetic': return '#f59e0b'; // Orange
        case 'reading/writing': return '#8b5cf6'; // Purple
        default: return '#9ca3af'; // Gray
      }
    }
  };

  // Function to add a learning milestone
  const addMilestone = (milestone) => {
    setMilestones(prev => [...prev, {
      id: Date.now(),
      text: milestone,
      timestamp: new Date().toLocaleTimeString()
    }]);
  };
  
  // The visualization functionality is now handled by the VisualizationController
  // which manages visualization state and data from voice interactions

  // Artifact panel functionality has been replaced by the VisualizationController

  // Toggle high contrast mode for accessibility
  const toggleHighContrastMode = () => {
    setHighContrastMode(!highContrastMode);
  };
  
  /**
   * Handle visualization requests coming from the voice agent function calls
   * This is a simplified version that only uses the VisualizationController
   */
  const handleVisualizationFromVoice = async (visualizationData) => {
    
    // Pass the visualization data to our new visualization system if handler exists
    if (onVisualizationRequest) {
      // Format the data for the visualization system
      const formattedData = {
        topic: visualizationData.topic || visualizationData.type || 'Educational Visualization',
        context: visualizationData.context || '',
        source: 'voice', // Add source for analytics
        timestamp: Date.now(),
        ...visualizationData
      };
      
      // Pass to parent VisualizationController
      onVisualizationRequest(formattedData);
      
      // Add a milestone for the user
      addMilestone(`Visualization for "${formattedData.topic}" has been created`);
      return;
    }
    
    // If no visualization handler is provided
    if (!visualizationData || !visualizationData.type) {
      console.error('⚠️ Invalid visualization data received from voice agent');
      return;
    }
    
    console.warn("No visualization controller available");
    
    // Add informational message to the user
    setMessages(prev => [...prev, {
      role: 'system',
      content: 'Visualization could not be created. Please try again later.'
    }]);
  };
  
  // This helper function has been removed as part of the migration to the new visualization system
  // Visualizations are now generated by the VisualizationController component

  // When a breakthrough is detected, add it as a milestone and show share toast
  useEffect(() => {
    if (breakthroughDetected) {
      addMilestone("Learning breakthrough detected! You've made a significant insight.");
      
      // Show achievement share toast
      setShowAchievementToast(true);
      
      // Auto-hide toast after 4 seconds
      const timer = setTimeout(() => {
        setShowAchievementToast(false);
      }, 4000);
      
      return () => clearTimeout(timer);
    }
  }, [breakthroughDetected]);

  // Add useEffect for body class management when visualization is visible
  // The VisualizationController will manage visibility and pass it back to us via props
  // This ensures the body class is properly set for the split-screen layout
  useEffect(() => {
    function handleVisualizationChange(event) {
      // The event might be dispatched by VisualizationController when visualization becomes visible/invisible
      if (event.detail && event.detail.isVisible) {
        document.body.classList.add('visualization-active');
      } else {
        document.body.classList.remove('visualization-active');
      }
    }
    
    // Listen for visualization visibility change events from VisualizationController
    window.addEventListener('visualization-visibility-change', handleVisualizationChange);
    
    return () => {
      // Cleanup on unmount
      document.body.classList.remove('visualization-active');
      window.removeEventListener('visualization-visibility-change', handleVisualizationChange);
    };
  }, []);

// Calculate session duration in seconds
  const getSessionDuration = () => {
    if (!sessionStartTime) return 0;
    return Math.floor((Date.now() - sessionStartTime) / 1000);
  };

  // Session duration state (updates every second during call)
  const [sessionDuration, setSessionDuration] = useState(0);

  useEffect(() => {
    if (connectionState === 'connected' && sessionStartTime) {
      const interval = setInterval(() => {
        setSessionDuration(getSessionDuration());
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setSessionDuration(0);
    }
  }, [connectionState, sessionStartTime]);

  // Mobile menu state for compact header
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Determine if we should show compact mobile header
  const showCompactMobileHeader = device.isMobile && connectionState === 'connected';
  // Focus layout applies only to an active Silent Mode chat on opted-in devices.
  const focusChat = focusLayout && isSilentMode && connectionState === 'connected';

return (
    <div
      className={`tutor-interface ${showCompactMobileHeader ? 'mobile-call-active' : ''} ${focusChat ? 'layout-focus' : ''}`}
      data-theme={isDarkMode ? 'dark' : 'light'}
    >
      {/* Mobile Compact Header - During active calls on mobile */}
      <AnimatePresence>
        {showCompactMobileHeader && (
          <MobileSessionHeader
            sessionDuration={sessionDuration}
            friends={friendsInSession}
            onMenuOpen={() => setShowMobileMenu(true)}
            onFriendClick={(friend) => {
              // Could show friend details or options
            }}
            connectionQuality="good"
          />
        )}
      </AnimatePresence>

      {/* Full Header - Hidden on mobile during calls */}
      <header className="tutor-header" style={{
        display: showCompactMobileHeader ? 'none' : 'flex',
      }}>
        <Link to="/" style={{ textDecoration: 'none' }}>
          <img
            src={isDarkMode ? "/images/TAPPY-logo-white.png" : "/images/TAPPY-logo-black.png"}
            alt="Tappy"
            className="tutor-header-logo"
          />
        </Link>

        <div className="tutor-header-center">
          {/* Connection status pill - 2025 Apple/Google/Discord style */}
          {isCallActive && connectionState === 'connected' && (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '6px 14px 6px 10px',
              background: 'rgba(16, 185, 129, 0.08)',
              borderRadius: '100px',
              border: 'none',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
            }}>
              {/* Pulsing dot with glow */}
              <span style={{
                position: 'relative',
                width: '8px',
                height: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                {/* Glow ring - animated */}
                <span style={{
                  position: 'absolute',
                  width: '14px',
                  height: '14px',
                  borderRadius: '50%',
                  background: 'rgba(16, 185, 129, 0.25)',
                  animation: 'statusPulse 2s ease-in-out infinite',
                }} />
                {/* Solid dot */}
                <span style={{
                  position: 'relative',
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: '#10b981',
                  boxShadow: '0 0 8px rgba(16, 185, 129, 0.5)',
                }} />
              </span>
              <span style={{
                fontSize: '13px',
                fontWeight: '500',
                color: '#047857',
                letterSpacing: '-0.01em',
              }}>
                Live with Mira
              </span>
            </div>
          )}
        </div>

        <div className="tutor-header-right">
          {/* Friends Online Widget - Discord-style presence */}
          <FriendsOnlineWidget
            user={user}
            onJoinFriend={handleJoinFriendSession}
            onAddFriend={() => setShowAddFriendModal(true)}
            onInviteFriend={() => setShowJoinFriendModal(true)}
            onFriendsUpdate={handleFriendsUpdate}
            isInSession={connectionState === 'connected'}
          />

          {/* Upgrade CTA - Free account users */}
          {user && isGuest && !isAnonymous && (
            <button
              className="upgrade-cta"
              onClick={() => setShowUpgradeModal(true)}
            >
              Upgrade
            </button>
          )}

          {/* Mini Dashboard Button - Free account users only */}
          {user && isGuest && !isAnonymous && (
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setShowMiniDashboard(!showMiniDashboard)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 12px',
                  background: showMiniDashboard
                    ? 'linear-gradient(135deg, rgba(41, 122, 146, 0.2), rgba(16, 185, 129, 0.2))'
                    : 'white',
                  border: showMiniDashboard
                    ? '1px solid rgba(41, 122, 146, 0.3)'
                    : '1px solid #e5e7eb',
                  borderRadius: '9999px',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  fontSize: '13px',
                  fontWeight: 500,
                  color: '#297A92'
                }}
              >
                <span style={{ fontSize: '14px' }}>📊</span>
                <span>My Progress</span>
                {guestSessionCount > 0 && (
                  <span style={{
                    background: '#10b981',
                    color: 'white',
                    padding: '2px 6px',
                    borderRadius: '10px',
                    fontSize: '11px',
                    fontWeight: 600
                  }}>
                    {guestSessionCount}
                  </span>
                )}
              </button>

              {/* Mini Dashboard Dropdown */}
              {showMiniDashboard && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '8px',
                  zIndex: 100
                }}>
                  <GuestMiniDashboard
                    learningStyleWeights={guestProfile.learningStyleWeights || learningStyleWeights}
                    sessionCount={guestProfile.totalSessions || guestSessionCount}
                    streakDays={guestProfile.streakDays || 0}
                    longestStreak={guestProfile.longestStreak || 0}
                    topicsCovered={guestProfile.topicsCovered || []}
                    totalMinutes={guestProfile.totalMinutes || 0}
                    totalInteractions={guestProfile.totalInteractions || 0}
                    miraOrbColor={miraOrbColor}
                    onUpgradeClick={() => {
                      setShowMiniDashboard(false);
                      setShowUpgradeModal(true);
                    }}
                  />
                </div>
              )}
            </div>
          )}

          {/* Theme Toggle Button - 2025 UX */}
          <button
            onClick={toggleTheme}
            className="header-icon-btn theme-toggle"
            aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
          >
            {isDarkMode ? <Icons.Sun /> : <Icons.Moon />}
          </button>

          {/* User Menu with Token Badge - 2025 UX for 6-12 graders */}
          {isAuthenticated && (
            <div style={{ position: 'relative' }}>
              {/* Token Badge - iOS notification style above user icon */}
              {tokenBalance > 0 && (
                <div
                  onClick={() => setShowTokenWallet(true)}
                  style={{
                    position: 'absolute',
                    top: '-8px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '2px',
                    padding: '2px 6px',
                    background: isDarkMode
                      ? 'linear-gradient(135deg, #F8C341, #ECC962)'
                      : 'linear-gradient(135deg, #ECC962, #F8C341)',
                    borderRadius: '10px',
                    fontSize: '11px',
                    fontWeight: 600,
                    color: '#3D4954',
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(236, 201, 98, 0.4)',
                    zIndex: 10,
                    whiteSpace: 'nowrap',
                    transition: 'transform 0.15s ease',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'translateX(-50%) scale(1.05)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'translateX(-50%)'}
                >
                  <span>🪙</span>
                  <span>{tokenBalance >= 1000 ? `${(tokenBalance / 1000).toFixed(1)}k` : tokenBalance}</span>
                </div>
              )}
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="header-icon-btn"
                aria-label="User menu"
                style={{ marginTop: tokenBalance > 0 ? '8px' : '0' }}
              >
                <Icons.User />
              </button>

              {/* Dropdown Menu */}
              <AnimatePresence>
                {showUserMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: -8, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -8, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    style={{
                      position: 'absolute',
                      top: '48px',
                      right: '0',
                      minWidth: '180px',
                      background: 'var(--color-bg-elevated)',
                      borderRadius: '12px',
                      border: 'none',
                      boxShadow: '0 4px 24px rgba(0, 0, 0, 0.12)',
                      overflow: 'hidden',
                      zIndex: 1000
                    }}
                  >
                    {/* User Info */}
                    <div style={{
                      padding: '12px 16px',
                      borderBottom: '1px solid var(--color-border-subtle, rgba(0,0,0,0.04))',
                      background: 'var(--color-bg-subtle)'
                    }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: 600,
                        color: 'var(--color-text)'
                      }}>
                        {user?.name || username || 'Student'}
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: 'var(--color-text-secondary)',
                        marginTop: '2px'
                      }}>
                        {FeatureTiers.formatTierName(userTier)} Plan
                      </div>
                    </div>

                    {/* Menu Items - Simple for 6-12 graders */}
                    <div style={{ padding: '8px' }}>
                      {/* My Account */}
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          window.location.href = '/subscription';
                        }}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          background: 'transparent',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          fontSize: '14px',
                          color: 'var(--color-text)',
                          transition: 'background 0.15s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-bg-subtle)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                          <circle cx="12" cy="7" r="4" />
                        </svg>
                        My Account
                      </button>

                      {/* Join Friend - Quick access to code entry */}
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          setShowJoinFriendModal(true);
                        }}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          background: 'transparent',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          fontSize: '14px',
                          color: 'var(--color-text)',
                          transition: 'background 0.15s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-bg-subtle)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                          <circle cx="9" cy="7" r="4" />
                          <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                        </svg>
                        Join Friend
                      </button>

                      {/* Wallet - Quick access to tokens */}
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          setShowTokenWallet(true);
                        }}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          background: 'transparent',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          fontSize: '14px',
                          color: 'var(--color-text)',
                          transition: 'background 0.15s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-bg-subtle)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{ fontSize: '18px' }}>🪙</span>
                          Wallet
                        </span>
                        <span style={{
                          fontSize: '13px',
                          fontWeight: 600,
                          color: 'var(--color-gold, #ECC962)',
                        }}>
                          {tokenBalance}
                        </span>
                      </button>

                      {/* Theme Toggle - Mobile only (hidden on desktop via CSS) */}
                      <button
                        onClick={() => {
                          toggleTheme();
                          // Don't close menu - let user see the change
                        }}
                        className="mobile-only-menu-item"
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          background: 'transparent',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          fontSize: '14px',
                          color: 'var(--color-text)',
                          transition: 'background 0.15s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-bg-subtle)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          {isDarkMode ? <Icons.Sun /> : <Icons.Moon />}
                          Appearance
                        </span>
                        <span style={{
                          fontSize: '12px',
                          color: 'var(--color-text-secondary)',
                        }}>
                          {isDarkMode ? 'Dark' : 'Light'}
                        </span>
                      </button>

                      {/* Divider */}
                      <div style={{
                        height: '1px',
                        background: 'var(--color-border)',
                        margin: '8px 0'
                      }} />

                      {/* Logout */}
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          if (onLogout) {
                            onLogout();
                          } else {
                            window.location.href = '/api/auth/logout';
                          }
                        }}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          background: 'transparent',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          fontSize: '14px',
                          color: 'var(--color-error)',
                          transition: 'background 0.15s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                          <polyline points="16 17 21 12 16 7" />
                          <line x1="21" y1="12" x2="9" y2="12" />
                        </svg>
                        Log Out
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Click outside to close */}
              {showUserMenu && (
                <div
                  onClick={() => setShowUserMenu(false)}
                  style={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 999
                  }}
                />
              )}
            </div>
          )}
        </div>
      </header>

      {/* Mobile Menu Dropdown - Shows when menu button tapped in compact header */}
      <AnimatePresence>
        {showMobileMenu && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowMobileMenu(false)}
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                zIndex: 1001,
              }}
            />
            {/* Menu */}
            <motion.div
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              style={{
                position: 'fixed',
                top: 'calc(36px + env(safe-area-inset-top, 0px) + 8px)',
                right: '12px',
                background: 'rgba(28, 28, 30, 0.95)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                borderRadius: '16px',
                padding: '8px',
                minWidth: '200px',
                boxShadow: '0 12px 48px rgba(0, 0, 0, 0.5)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                zIndex: 1002,
              }}
            >
              {/* Token Balance */}
              {isAuthenticated && (
                <button
                  onClick={() => {
                    setShowMobileMenu(false);
                    setShowTokenWallet(true);
                  }}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 16px',
                    background: 'transparent',
                    border: 'none',
                    borderRadius: '12px',
                    color: 'white',
                    fontSize: '15px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <span style={{ fontSize: '18px' }}>🪙</span>
                  <span>{tokenBalance} tokens</span>
                </button>
              )}

              {/* Streak */}
              {isAuthenticated && streakDays > 0 && (
                <button
                  onClick={() => {
                    setShowMobileMenu(false);
                    setShowTokenWallet(true);
                  }}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 16px',
                    background: 'transparent',
                    border: 'none',
                    borderRadius: '12px',
                    color: 'white',
                    fontSize: '15px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <span style={{ fontSize: '18px' }}>🔥</span>
                  <span>{streakDays} day streak</span>
                </button>
              )}

              {/* Divider */}
              <div style={{
                height: '1px',
                background: 'rgba(255, 255, 255, 0.1)',
                margin: '8px 0',
              }} />

              {/* End Session */}
              <button
                onClick={() => {
                  setShowMobileMenu(false);
                  handleEndSession();
                }}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 16px',
                  background: 'transparent',
                  border: 'none',
                  borderRadius: '12px',
                  color: '#ef4444',
                  fontSize: '15px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                <span>End Session</span>
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Hidden audio element - TTS playback disabled */}
      <audio 
        ref={audioRef}
        style={{ display: 'none' }}
      />
      
      {/* Immersive Learning Aquarium World */}
      <style>
        {`
          @keyframes aquariumFlow {
            0%, 100% { background-position: 0% 0%, 0% 0%, 0% 0%, 0% 0%; }
            25% { background-position: 0% 0%, 10% 5%, -5% 10%, 8% -3%; }
            50% { background-position: 0% 0%, -5% 10%, 10% -5%, -8% 8%; }
            75% { background-position: 0% 0%, 8% -5%, -10% 8%, 5% -10%; }
          }
          
          @keyframes aquariumBreath {
            0%, 100% { transform: translateY(0px) scale(1); }
            50% { transform: translateY(-2px) scale(1.005); }
          }
          
          @keyframes gentleFloat {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            25% { transform: translateY(-4px) rotate(0.5deg); }
            50% { transform: translateY(-2px) rotate(0deg); }
            75% { transform: translateY(-6px) rotate(-0.5deg); }
          }
          
          .aquarium-container {
            background: rgba(255, 255, 255, 0.15) !important;
            backdrop-filter: blur(20px) saturate(120%) !important;
            border: 1px solid rgba(255, 255, 255, 0.2) !important;
            box-shadow: 
              0 8px 32px rgba(171, 202, 202, 0.2),
              inset 0 1px 0 rgba(255, 255, 255, 0.3) !important;
            animation: aquariumBreath 8s ease-in-out infinite !important;
          }
          
          .voice-assistant-box.aquarium-container:hover {
            transform: scale(1.05) translateY(-3px) !important;
            box-shadow: 0 12px 40px rgba(41, 122, 146, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.4) !important;
            animation: none !important;
          }
          
          .floating-element {
            animation: gentleFloat 6s ease-in-out infinite !important;
            transform: translateZ(0) !important;
            will-change: transform !important;
          }
          
          @media (prefers-reduced-motion: reduce) {
            * {
              animation-duration: 0.01ms !important;
              animation-iteration-count: 1 !important;
            }
          }
        `}
      </style>
      {/* Token display hidden for now - will be restored when bounty feature is complete */}
      {/* {isCallActive && (
        <div style={{
          position: 'fixed',
          top: '16px',
          right: '16px',
          zIndex: 999,
          background: 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(10px)',
          borderRadius: '8px',
          padding: '8px 12px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(41, 122, 146, 0.08)',
          fontSize: '0.85rem',
          opacity: 0.7,
          transition: 'opacity 0.2s ease',
          cursor: 'pointer'
        }}
        onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
        onMouseLeave={(e) => e.currentTarget.style.opacity = '0.7'}
        >
          <TokenDisplay
            isAuthenticated={isAuthenticated}
            username={username}
            userTier={userTier}
            hasFeatureAccess={hasFeatureAccess}
            user={user}
            minimal={true}
          />
        </div>
      )} */}

      {/* Main content area - production pattern */}
      <div className="voice-first-interface" style={{
        flex: 1,
        width: '100%',
        maxWidth: '1400px',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        // Focus layout stacks from the top: centering content taller than
        // the container overflowed both ends (orb clipped under the header,
        // input pushed below the fold).
        justifyContent: focusChat ? 'flex-start' : 'center',
        alignItems: 'center',
        padding: focusChat
          ? '8px 12px 10px'
          : (isCallActive ? (isSilentMode ? '12px 0 0 0' : '24px 24px 100px 24px') : '24px'), // Silent mode: minimal padding (no voice controls); Voice mode: bottom padding for controls
        position: 'relative',
        minHeight: isSilentMode ? 0 : 'calc(100vh - 200px)', // Silent mode: flex-fill, Voice: account for footer
        overflow: isSilentMode ? 'hidden' : undefined
      }}>
        {/* Teacher-assigned session context (renders nothing for general tutoring) */}
        <AssignmentBanner />
        {/* Clean centered content */}
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: isSilentMode ? 'stretch' : 'center',
            alignItems: 'center',
            width: '100%',
            maxWidth: isSilentMode ? '100%' : '800px',
            position: 'relative',
            flex: isSilentMode ? 1 : undefined,
            minHeight: isSilentMode ? 0 : undefined,
            height: isSilentMode ? '100%' : undefined
          }}
        >
          {/* Mode toggle button - clean, Apple-style design (only shown if classroom mode is enabled) */}
          {isCallActive && featureFlags.classroom_mode && (
            <motion.button
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              onClick={async () => {
                if (!voiceInputRef.current?.switchMode) {
                  console.error('switchMode not available');
                  return;
                }

                const newMode = currentMode === 'voice' ? 'classroom' : 'voice';

                try {
                  await voiceInputRef.current.switchMode(newMode);
                  setCurrentMode(newMode);
                  showToastNotification(
                    `Switched to ${newMode === 'voice' ? 'Voice Mode 🎤' : 'Text Mode 📝'}`,
                    'success'
                  );
                } catch (error) {
                  console.error('Error switching mode:', error);
                  showToastNotification('Failed to switch mode', 'error');
                }
              }}
              style={{
                position: 'absolute',
                top: '-60px',
                right: '0px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 18px',
                background: 'var(--color-bg-elevated, rgba(255, 255, 255, 0.9))',
                backdropFilter: 'blur(20px)',
                border: 'none',
                borderRadius: '20px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#297A92',
                cursor: 'pointer',
                boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
                transition: 'all 0.2s ease',
                zIndex: 1000
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(41, 122, 146, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 12px rgba(0, 0, 0, 0.08)';
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', color: '#297A92' }}>
                {currentMode === 'classroom' ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                    <line x1="12" x2="12" y1="19" y2="22"/>
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="4" width="20" height="16" rx="2"/>
                    <path d="M6 8h.01"/>
                    <path d="M10 8h.01"/>
                    <path d="M14 8h.01"/>
                    <path d="M18 8h.01"/>
                    <path d="M8 12h.01"/>
                    <path d="M12 12h.01"/>
                    <path d="M16 12h.01"/>
                    <path d="M7 16h10"/>
                  </svg>
                )}
              </span>
              <span>
                {currentMode === 'classroom' ? 'Switch to Voice' : 'Switch to Text'}
              </span>
            </motion.button>
          )}

          {achievement && (
            <AchievementPopup
              achievement={achievement}
              onClose={() => setAchievement(null)}
            />
          )}
          
          {/* Simple status indicator */}
          <AnimatePresence>
            {(isPlayingAudio || loading) && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className={`status-indicator aquarium-container floating-element ${isPlayingAudio ? 'speaking' : 'processing'}`}
                style={{
                  padding: '12px 24px',
                  borderRadius: '20px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'rgba(41, 122, 146, 0.9)',
                  margin: '0 auto 20px auto',
                  animationDelay: '1s'
                }}
              >
                {isPlayingAudio ? 'Speaking...' : 'Processing...'}
              </motion.div>
            )}
          </AnimatePresence>
          
          {/* Connection loading state - modern UX */}
          {(connectionState === 'connecting' || connectionState === 'disconnecting' || connectionState === 'error') && (
            <ConnectionLoadingState
              state={connectionState}
              onTimeout={() => {
                // Use ref to get current state (avoids stale closure from when callback was created)
                const currentState = connectionStateRef.current;
                if (currentState === 'connecting') {
                  setConnectionState('error');
                  showToastNotification('Connection timeout - please try again', 'error');
                } else if (currentState === 'disconnecting') {
                  // Force cleanup after timeout
                  setConnectionState('idle');
                  setIsCallActive(false);
                }
                // If currentState is 'connected', do nothing - Mira joined successfully!
              }}
              onRetry={() => {
                setConnectionState('idle');
                setIsCallActive(false);
                // User can click the card again to retry
              }}
            />
          )}

          {/* Social Learning Hub - 2025 Discord/FaceTime-inspired idle state */}
          {connectionState === 'idle' && !isCallActive && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '24px',
              maxWidth: '440px',
              width: '100%',
              padding: '0 16px'
            }}>
              {/* Streak Progress - Duolingo-inspired gamification */}
              {isAuthenticated && streakDays > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '10px 16px',
                    background: 'rgba(251, 191, 36, 0.06)',
                    borderRadius: '12px',
                    border: 'none',
                    width: '100%'
                  }}
                >
                  <span style={{ fontSize: '20px' }}>🔥</span>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#92400e'
                    }}>
                      {streakDays} day streak!
                    </div>
                    <div style={{
                      marginTop: '4px',
                      height: '4px',
                      background: 'rgba(251, 191, 36, 0.2)',
                      borderRadius: '2px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${Math.min((streakDays % 7) / 7 * 100, 100)}%`,
                        height: '100%',
                        background: 'linear-gradient(90deg, #f59e0b, #fbbf24)',
                        borderRadius: '2px',
                        transition: 'width 0.3s ease'
                      }} />
                    </div>
                  </div>
                  <span style={{
                    fontSize: '12px',
                    color: '#b45309',
                    fontWeight: 500
                  }}>
                    {7 - (streakDays % 7)} to next badge
                  </span>
                </motion.div>
              )}

              {/* Mira Avatar + Greeting - Animated, friendly */}
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.4 }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '12px'
                }}
              >
                {/* Animated Mira avatar */}
                <motion.div
                  animate={{
                    boxShadow: [
                      '0 8px 24px rgba(41, 122, 146, 0.25)',
                      '0 12px 32px rgba(41, 122, 146, 0.35)',
                      '0 8px 24px rgba(41, 122, 146, 0.25)'
                    ]
                  }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  style={{
                    width: '88px',
                    height: '88px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #297A92 0%, #4BB3D9 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '3px solid rgba(255, 255, 255, 0.6)'
                  }}
                >
                  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                    <line x1="12" x2="12" y1="19" y2="22"/>
                  </svg>
                </motion.div>

                {/* Dynamic greeting */}
                <div style={{ textAlign: 'center' }}>
                  <h2 style={{
                    fontSize: '22px',
                    fontWeight: 600,
                    color: '#1e293b',
                    margin: '0 0 4px 0',
                    letterSpacing: '-0.02em'
                  }}>
                    {(() => {
                      const hour = new Date().getHours();
                      if (hour < 12) return "Good morning!";
                      if (hour < 17) return "Hey there!";
                      return "Good evening!";
                    })()}
                  </h2>
                  <p style={{
                    fontSize: '15px',
                    color: '#64748b',
                    margin: 0
                  }}>
                    {user?.name ? `Ready to learn, ${user.name.split(' ')[0]}?` : "What are you working on?"}
                  </p>
                </div>
              </motion.div>

              {/* FRIENDS ROW - Snapchat-style, always visible, zero taps to see */}
              {/* Gen Z UX: Friends are VISIBLE, not hidden in a dropdown */}
              {isAuthenticated && onlineFriends.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.1 }}
                  style={{
                    display: 'flex',
                    gap: '20px',
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                    width: '100%'
                  }}
                >
                  {onlineFriends.slice(0, 4).map((friend, index) => {
                    // can_join means they're in an active session and allow friends
                    const isStudying = friend.can_join;
                    const isJoining = joiningFriendId === friend.id;
                    const initial = (friend.display_name || friend.username)?.charAt(0)?.toUpperCase() || '?';
                    const firstName = (friend.display_name || friend.username)?.split(' ')[0] || 'Friend';

                    return (
                      <motion.button
                        key={friend.id}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.2, delay: 0.05 * index }}
                        whileHover={!isJoining ? { scale: 1.08, y: -4 } : {}}
                        whileTap={!isJoining ? { scale: 0.95 } : {}}
                        onClick={() => {
                          if (isStudying && !isJoining) {
                            handleJoinFriendSession(friend);
                          }
                        }}
                        disabled={isJoining}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '6px',
                          background: 'none',
                          border: 'none',
                          cursor: isStudying && !isJoining ? 'pointer' : 'default',
                          padding: '4px',
                          opacity: isJoining ? 0.8 : 1
                        }}
                      >
                        {/* Avatar with status ring */}
                        <div style={{ position: 'relative' }}>
                          <div style={{
                            width: '56px',
                            height: '56px',
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #297A92, #6DB4CD)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '20px',
                            fontWeight: 600,
                            border: isStudying
                              ? '3px solid #30d158'
                              : '3px solid #e2e8f0',
                            boxShadow: isStudying
                              ? '0 0 16px rgba(48, 209, 88, 0.4)'
                              : 'none',
                            transition: 'all 0.2s ease'
                          }}>
                            {isJoining ? (
                              /* Loading spinner */
                              <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                                style={{
                                  width: '24px',
                                  height: '24px',
                                  border: '3px solid rgba(255,255,255,0.3)',
                                  borderTopColor: 'white',
                                  borderRadius: '50%'
                                }}
                              />
                            ) : initial}
                          </div>
                          {/* Status dot - hide when joining */}
                          {!isJoining && (
                            <div style={{
                              position: 'absolute',
                              bottom: '2px',
                              right: '2px',
                              width: '14px',
                              height: '14px',
                              borderRadius: '50%',
                              background: isStudying ? '#30d158' : '#94a3b8',
                              border: '2px solid white',
                              boxShadow: isStudying
                                ? '0 0 8px rgba(48, 209, 88, 0.6)'
                                : 'none'
                            }} />
                          )}
                        </div>
                        {/* Name */}
                        <span style={{
                          fontSize: '12px',
                          fontWeight: 500,
                          color: isStudying ? '#166534' : '#64748b',
                          maxWidth: '64px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {firstName}
                        </span>
                        {/* Status */}
                        <span style={{
                          fontSize: '10px',
                          fontWeight: 500,
                          color: isJoining ? '#297A92' : (isStudying ? '#30d158' : '#94a3b8'),
                          textTransform: 'uppercase',
                          letterSpacing: '0.02em'
                        }}>
                          {isJoining ? '● joining...' : (isStudying ? '● studying' : '○ online')}
                        </span>
                      </motion.button>
                    );
                  })}
                  {onlineFriends.length > 4 && (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '4px'
                    }}>
                      <div style={{
                        width: '56px',
                        height: '56px',
                        borderRadius: '50%',
                        background: '#f1f5f9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#64748b',
                        fontSize: '14px',
                        fontWeight: 600,
                        border: '3px solid #e2e8f0'
                      }}>
                        +{onlineFriends.length - 4}
                      </div>
                      <span style={{ fontSize: '12px', color: '#94a3b8' }}>more</span>
                    </div>
                  )}
                </motion.div>
              )}

              {/* Language selector - only shown if school has bilingual support */}
              {user?.available_languages && user.available_languages.length > 1 && (
                <motion.div
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, delay: 0.1 }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: '8px 0'
                  }}
                >
                  {user.available_languages.map((lang) => (
                    <button
                      key={lang}
                      onClick={() => setSessionLanguage(lang)}
                      style={{
                        padding: '6px 16px',
                        borderRadius: '20px',
                        border: sessionLanguage === lang ? '2px solid #297A92' : '1px solid rgba(148, 163, 184, 0.3)',
                        background: sessionLanguage === lang ? 'rgba(41, 122, 146, 0.15)' : 'transparent',
                        color: sessionLanguage === lang ? '#297A92' : '#94a3b8',
                        fontSize: '13px',
                        fontWeight: sessionLanguage === lang ? 600 : 400,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {lang === 'en' ? 'English' : lang === 'es' ? 'Espanol' : lang.toUpperCase()}
                    </button>
                  ))}
                </motion.div>
              )}

              {/* MAIN CTA - Always "Start Studying" */}
              {/* Friends row handles tap-to-join, this is for starting your own session */}
              <motion.button
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.3, delay: 0.15 }}
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  if (connectionState !== 'idle') return;
                  setCurrentMode('voice');
                  playActivationSound();
                  setConnectionState('connecting');
                  startTransition(() => {
                    setIsCallActive(true);
                    if (!voiceComponentInitialized) {
                      setVoiceComponentInitialized(true);
                    }
                  });
                }}
                style={{
                  width: '100%',
                  padding: '18px 24px',
                  background: 'linear-gradient(135deg, #297A92 0%, #3d9bb0 100%)',
                  borderRadius: '16px',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  boxShadow: '0 8px 24px rgba(41, 122, 146, 0.3)',
                  transition: 'all 0.2s ease'
                }}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                  <line x1="12" x2="12" y1="19" y2="22"/>
                </svg>
                <span style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: 'white',
                  letterSpacing: '-0.01em'
                }}>
                  Start Studying
                </span>
              </motion.button>

              {/* Silent Mode - Text-only for classroom use */}
              {/* Gen Z naming: "Silent" is familiar (phone silent mode), not "Classroom Mode" */}
              <motion.button
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.12 }}
                whileHover={{ scale: 1.02, y: -1 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  if (connectionState !== 'idle') return;
                  // Silent Mode: No LiveKit, just direct API chat
                  setIsSilentMode(true);
                  playActivationSound();
                  setConnectionState('connected'); // Skip connecting, go straight to connected
                  setSessionStartTime(Date.now()); // Track session start
                }}
                style={{
                  width: '100%',
                  padding: '14px 20px',
                  background: 'var(--color-bg-subtle)',
                  borderRadius: '12px',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  transition: 'all 0.2s ease'
                }}
              >
                {/* Silent/mute icon */}
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                  <line x1="12" x2="12" y1="19" y2="22"/>
                  <line x1="2" x2="22" y1="2" y2="22" stroke="#6b7280" strokeWidth="2"/>
                </svg>
                <span style={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#6b7280',
                  letterSpacing: '-0.01em'
                }}>
                  Go Silent (text only)
                </span>
              </motion.button>

              {/* Teach Mira - Feynman technique entry point */}
              {/* Purple gradient = achievement/mastery, +50 badge = gamification incentive */}
              {isAuthenticated && (
                <motion.button
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.18 }}
                  whileHover={{ scale: 1.02, y: -1 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowTeachItBack(true)}
                  style={{
                    width: '100%',
                    padding: '14px 20px',
                    background: 'rgba(147, 51, 234, 0.06)',
                    borderRadius: '12px',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {/* Lightbulb/brain icon for teaching */}
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9333ea" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5"/>
                    <path d="M9 18h6"/>
                    <path d="M10 22h4"/>
                  </svg>
                  <span style={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#7c3aed',
                    letterSpacing: '-0.01em'
                  }}>
                    Teach Mira
                  </span>
                  {/* Token reward badge - Duolingo-style incentive */}
                  <span style={{
                    padding: '3px 8px',
                    background: 'linear-gradient(135deg, #9333ea, #a855f7)',
                    borderRadius: '10px',
                    fontSize: '11px',
                    fontWeight: 600,
                    color: 'white',
                    boxShadow: '0 2px 8px rgba(147, 51, 234, 0.3)'
                  }}>
                    +50
                  </span>
                </motion.button>
              )}

              {/* Clutch Time - Exam prep mode */}
              {isAuthenticated && (
                <motion.button
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.2 }}
                  whileHover={{ scale: 1.02, y: -1 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowClutchTimeModal(true)}
                  style={{
                    width: '100%',
                    padding: '14px 20px',
                    background: 'rgba(245, 158, 11, 0.06)',
                    borderRadius: '12px',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {/* Lightning bolt icon for Clutch Time */}
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                  </svg>
                  <span style={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#f59e0b'
                  }}>
                    Clutch Time
                  </span>
                  <span style={{
                    fontSize: '11px',
                    color: '#92400e',
                    opacity: 0.8
                  }}>
                    Exam Prep
                  </span>
                </motion.button>
              )}

              {/* Subtle code entry - for when you have a friend's code */}
              <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, delay: 0.22 }}
                onClick={() => setShowJoinFriendModal(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '13px',
                  color: '#94a3b8',
                  padding: '8px',
                  transition: 'color 0.15s ease'
                }}
                onMouseEnter={(e) => e.target.style.color = '#64748b'}
                onMouseLeave={(e) => e.target.style.color = '#94a3b8'}
              >
                Have a friend's code? <span style={{ color: '#297A92' }}>Enter it →</span>
              </motion.button>

              {/* Guest CTA - Sign in to unlock social features */}
              {!isAuthenticated && (
                <motion.div
                  initial={{ y: 10, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.3, delay: 0.2 }}
                  style={{
                    width: '100%',
                    padding: '16px',
                    background: 'rgba(41, 122, 146, 0.04)',
                    borderRadius: '12px',
                    border: 'none',
                    textAlign: 'center'
                  }}
                >
                  <p style={{
                    fontSize: '14px',
                    color: '#64748b',
                    margin: '0 0 12px 0'
                  }}>
                    Sign in to study with friends and track your progress
                  </p>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => window.location.href = '/login'}
                    style={{
                      padding: '10px 20px',
                      background: '#297A92',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    Sign in
                  </motion.button>
                </motion.div>
              )}
            </div>
          )}

          {/* Phase 2 ActiveSessionView - 2025 Orb Design.
              In focus layout the orb moves INTO the chat header instead. */}
          {connectionState === 'connected' && !focusChat && (
            <ActiveSessionView
              isConnected={connectionState === 'connected'}
              isConnecting={connectionState === 'connecting'}
              agentState={agentState}
              friends={friendsInSession}
              onlineFriends={onlineFriends}  // Friends available to invite (visible without dropdown)
              onJoinFriend={handleJoinFriendSession}
              onInviteMore={handleInviteFriend}  // 2025 FaceTime style - invite more during session
              user={user}
              isClutchTime={clutchTimeActive}  // Amber/orange orb when in Clutch Time mode
              // Silent Mode - text-only for classroom use
              isSilentMode={isSilentMode}
              onToggleSilentMode={() => setIsSilentMode(!isSilentMode)}
              // Mira customization from token shop (Gen Z rewards)
              miraOrbColor={miraOrbColor}
              miraGlowEffect={miraGlowEffect}
              miraEyeStyle={miraEyeStyle}
              // 2026 Curiosity Ambient Feedback - affects orb physics (Part 5)
              curiosityMomentum={curiosityMomentum}
            />
          )}

          {/* Silent Mode chat - shows when in Silent Mode (direct API, no LiveKit) */}
          {connectionState === 'connected' && isSilentMode && (
            <SilentModeChat
              userId={user?.id || 'anonymous'}
              /* Stable session id from state — `session-${Date.now()}` here
                 was re-evaluated on EVERY render, so each message landed in
                 a brand-new server session. Analytics showed only 0- and
                 1-question sessions (real multi-turn conversations shredded
                 into one row each) and server-side history never persisted. */
              sessionId={classroomSessionId || sessionId}
              onStudentMessage={(text) => {
                // Same curiosity heuristic voice uses — no new logic.
                const signal = detectCuriositySignal(text);
                if (signal > 0) updateCuriosityMomentum(signal);
              }}
              headerAvatar={focusChat ? (
                <ActiveSessionView
                  variant="avatar"
                  isConnected
                  agentState={agentState}
                  isClutchTime={clutchTimeActive}
                  miraOrbColor={miraOrbColor}
                  miraGlowEffect={miraGlowEffect}
                  curiosityMomentum={curiosityMomentum}
                />
              ) : null}
              anonymousId={anonymousId || ''}
              clutchTimeActive={clutchTimeActive}
              clutchTimeSubject={clutchTimeSubject}
              clutchTimeTopics={clutchTimeTopics}
              clutchTimeTestDate={clutchTimeTestDate}
              onEndSession={() => {
                setConnectionState('idle');
                setIsSilentMode(false);
                setSessionStartTime(null);
              }}
              onSessionLimitReached={({ gateType, isAnonymous: isAnonUser, sessionCount, message }) => {
                // Mirror voice mode's onVoiceLimitReached behavior
                setConnectionState('idle');
                setIsSilentMode(false);
                setSessionStartTime(null);
                setGuestSessionCount(sessionCount);
                setGateType(gateType || 'subscribe');

                if (gateType === 'create_account') {
                  setShowLearningSnapshot(true);
                } else {
                  setShowUpgradeModal(true);
                }
              }}
              onOpenClutchTime={() => {
                // Track that Clutch Time was launched from Silent Mode
                clutchTimeLaunchedFromSilent.current = true;
                // Temporarily exit Silent Mode UI so ClutchTimeModal can render
                setConnectionState('idle');
                setIsSilentMode(false);
                setSessionStartTime(null);
                setShowClutchTimeModal(true);
              }}
            />
          )}

          {/* Voice input - persistent after first initialization to prevent mount/unmount cycles */}
          {/* Hide during disconnecting so ConnectionLoadingState is visible */}
          {voiceComponentInitialized && connectionState !== 'disconnecting' && (
            <div style={{
              display: 'block',
              width: '100%',
              maxWidth: currentMode === 'classroom' ? '600px' : '100%',
              margin: '0 auto'
            }}>
              <LiveKitVoiceInput
                ref={voiceInputRef}
                onTranscript={handleVoiceTranscript}
                onVisualizationRequest={handleVisualizationFromVoice}
                onSoundEffects={handleSoundEffects}
                isDisabled={loading || isPlayingAudio || !isCallActive}
                isMuted={isMuted}
                userClicked={isCallActive}
                initialDisabled={!isCallActive}
                userTier={userTier}
                user={user}
                showVideoCollaboration={videoCollaborationEnabled}
                forceMinimizeVideoPanel={shouldMinimizeVideoPanel}
                classroomMode={currentMode === 'classroom'}
                classroomSessionId={classroomSessionId}
                silentMode={isSilentMode}
                onSilentModeChange={setIsSilentMode}
                teachItBackMode={teachItBackActive}
                teachTopic={currentTeachTopic}
                clutchTimeMode={clutchTimeActive}
                clutchTimeSubject={clutchTimeSubject}
                clutchTimeTopics={clutchTimeTopics}
                clutchTimeTestDate={clutchTimeTestDate}
                sessionLanguage={sessionLanguage}
                /* Phase 2: FaceTime with Mira - Callback handlers
                 *
                 * Connection flow (Mira must be ready before user talks):
                 * 1. User clicks "Start" → connectionState = 'connecting'
                 * 2. LiveKit room connects → onConnectionChange(true) - stay in 'connecting'
                 * 3. Mira (agent) joins → onAgentJoinedChange(true) → connectionState = 'connected'
                 *
                 * Disconnection flow (room cleanup takes ~20s):
                 * 1. User clicks "End" → connectionState = 'disconnecting'
                 * 2. Disconnect called → onConnectionChange(false) - cleanup timer in end handler
                 * 3. After 20s cleanup → connectionState = 'idle'
                 */
                onConnectionChange={(connected, roomName) => {
                  if (connected) {
                    // Room connected - capture room name for presence updates
                    // This enables friends to join via tap-to-join
                    setCurrentRoomName(roomName || null);
                    // Keep 'connecting' state - onAgentJoinedChange will advance to 'connected'
                  } else {
                    // Room disconnected - clear room name
                    setCurrentRoomName(null);
                    // IMPORTANT: Use ref to get current state (avoids stale closure)
                    // The state variable 'connectionState' captures value at callback creation time,
                    // but connectionStateRef.current is updated synchronously via useEffect
                    const currentState = connectionStateRef.current;

                    // Only reset state if NOT in disconnecting flow
                    // When user clicks End, we set 'disconnecting' and want the spinner to show for 20s
                    if (currentState !== 'disconnecting') {
                      setSessionStartTime(null);
                      setAgentState('idle');
                      setFriendsInSession([]);
                      setFriendsWhoStudied([]);
                    }
                    // If disconnecting, do nothing - the 20s timer in the button handler will set idle
                  }
                }}
                onAgentStateChange={(state) => setAgentState(state)}
                onAgentJoinedChange={(joined) => {
                  if (joined) {
                    // Mira is ready! NOW we can show connected state
                    setConnectionState('connected');
                    setSessionStartTime(Date.now());
                    setAgentState('listening');
                    // Reset session tracking for conversion funnel
                    setSessionTopics([]);
                    setSessionCognitiveState('building_confidence');
                  } else {
                    // Mira left the room
                    setAgentState('idle');
                  }
                }}
                onFriendsChange={(friends) => {
                  setFriendsInSession(friends);
                  setFriendParticipantsCount(friends.length);
                }}
                onVoiceLimitReached={({ gateType, isAnonymous: isAnonUser, sessionCount, message }) => {
                  // Guest/free user hit session limit - reset UI state and show appropriate gate
                  setConnectionState('idle');
                  setIsCallActive(false);
                  setGuestSessionCount(sessionCount);
                  setGateType(gateType || 'subscribe');

                  if (gateType === 'create_account') {
                    // Anonymous user - show LearningSnapshot with create account CTA
                    setShowLearningSnapshot(true);
                  } else {
                    // Free account user - show upgrade/subscribe modal
                    setShowUpgradeModal(true);
                  }
                }}
              />
            </div>
          )}
          
          {/* MOBILE: MobileControlBar (2025 thumb-zone design) */}
          {isCallActive && device.isMobile && (
            <MobileControlBar
              isMuted={isMuted}
              isCameraOn={false}
              isScreenSharing={isScreenSharing}
              friendCount={friendParticipantsCount}
              isVisible={controlsAutoHide.isVisible}
              showCameraButton={false}
              showScreenShareButton={true}
              showInviteButton={true}
              canScreenShare={hasFeatureAccess?.('SCREEN_SHARING')}
              canInviteFriends={hasFeatureAccess?.('FRIEND_TRAINING_HOST')}
              user={user}
              onShowToast={showToastNotification}
              onMuteToggle={() => {
                haptics.medium();
                setIsMuted(!isMuted);
              }}
              onScreenShareToggle={async () => {
                haptics.medium();
                try {
                  const roomInfo = voiceInputRef.current?.getRoomInfo?.();
                  if (!roomInfo || !roomInfo.localParticipant) {
                    showToastNotification('Please start a voice session with Mira first!', 'warning');
                    return;
                  }
                  const newState = !isScreenSharing;
                  await roomInfo.localParticipant.setScreenShareEnabled(newState);
                  setIsScreenSharing(newState);
                  if (newState) {
                    showToastNotification('Screen sharing started! Mira can now see your homework.', 'success');
                  } else {
                    showToastNotification('Screen sharing stopped', 'info');
                  }
                } catch (error) {
                  console.error('📺 Screen sharing error:', error);
                  if (error.name === 'NotAllowedError') {
                    showToastNotification('Screen sharing permission denied', 'error');
                  } else {
                    showToastNotification('Screen sharing failed. Please try again.', 'error');
                  }
                }
              }}
              onInviteFriend={() => {
                haptics.light();
                handleInviteFriend();
              }}
              onEndSession={handleEndSession}
            />
          )}

          {/* DESKTOP: Centered floating control bar (2025 Google Meet/FaceTime style) */}
          {isCallActive && !device.isMobile && (
            <div style={{
              position: 'fixed',
              bottom: '24px',
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 16px',
              background: 'rgba(32, 32, 32, 0.9)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
              zIndex: 1000
            }}>
              {/* Invite Friend button - opens premium StudyCodeSheet */}
              <FeatureGate
                feature="FRIEND_TRAINING_HOST"
                user={user}
                mode="inline"
                showUpgradeMessage={true}
                onShowToast={showToastNotification}
              >
                <StudyCallButton
                  user={user}
                  userTier={userTier}
                  sessionContext={{ subject: 'homework', current_problem: 'Study session' }}
                  onCodeGenerated={() => {
                    setVideoCollaborationEnabled(true);
                  }}
                  onCodeToastChange={(isShowing) => {
                    setShouldMinimizeVideoPanel(isShowing);
                  }}
                  onOpenStudyCodeSheet={handleInviteFriend}
                  compact={true}
                />
              </FeatureGate>

              {/* Quick Copy Link - Discord style 1-click */}
              {/* TODO: Hidden until link sharing UX is finalized for Gen Z/Y */}
              {/* Needs storyboarding: iMessage, Snapchat, Instagram DM flows */}
              {false && (
              <FeatureGate
                feature="FRIEND_TRAINING_HOST"
                user={user}
                mode="inline"
                showUpgradeMessage={true}
                onShowToast={showToastNotification}
              >
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={async () => {
                    try {
                      // Generate code and copy link in one click
                      const response = await fetch('/api/create-study-code', {
                        method: 'POST',
                        credentials: 'include',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          user_id: user?.id || 'guest',
                          student_name: user?.name || 'Student',
                          subject: 'homework',
                          problem_context: 'Study session'
                        })
                      });
                      if (response.ok) {
                        const data = await response.json();
                        const joinUrl = `${window.location.origin}/join/${data.code}`;
                        const shareText = `Join my study session on Tappy!\n\nCode: ${data.code}\n\nOr tap: ${joinUrl}`;
                        await navigator.clipboard.writeText(shareText);
                        setVideoCollaborationEnabled(true);
                        showToastNotification('Invite link copied!', 'success');
                      }
                    } catch (error) {
                      console.error('Quick copy failed:', error);
                      showToastNotification('Failed to copy link', 'error');
                    }
                  }}
                  style={{
                    cursor: 'pointer',
                    width: '48px',
                    height: '48px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.15)',
                    transition: 'background 0.2s ease'
                  }}
                  title="Quick copy invite link"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                  </svg>
                </motion.div>
              </FeatureGate>
              )}

              {/* Divider */}
              <div style={{ width: '1px', height: '32px', background: 'rgba(255,255,255,0.15)' }} />

              {/* Mute button */}
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsMuted(!isMuted)}
                style={{
                  cursor: 'pointer',
                  width: '48px',
                  height: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  background: isMuted ? '#ff453a' : 'rgba(255, 255, 255, 0.15)',
                  transition: 'background 0.2s ease'
                }}
                title={isMuted ? 'Unmute' : 'Mute'}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  {isMuted ? (
                    <path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z" fill="white"/>
                  ) : (
                    <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" fill="white"/>
                  )}
                </svg>
              </motion.div>

              {/* Screen Share button */}
              <FeatureGate
                feature="SCREEN_SHARING"
                user={user}
                mode="inline"
                showUpgradeMessage={true}
                onShowToast={showToastNotification}
              >
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={async () => {
                    try {
                      const roomInfo = voiceInputRef.current?.getRoomInfo?.();
                      if (!roomInfo || !roomInfo.localParticipant) {
                        showToastNotification('Please start a voice session first', 'warning');
                        return;
                      }
                      const newState = !isScreenSharing;
                      await roomInfo.localParticipant.setScreenShareEnabled(newState);
                      setIsScreenSharing(newState);
                      showToastNotification(newState ? 'Screen sharing started' : 'Screen sharing stopped', newState ? 'success' : 'info');
                    } catch (error) {
                      if (error.name === 'NotAllowedError') {
                        showToastNotification('Screen sharing permission denied', 'error');
                      } else {
                        console.error('Screen sharing error:', error);
                        showToastNotification('Screen sharing failed', 'error');
                      }
                    }
                  }}
                  style={{
                    cursor: 'pointer',
                    width: '48px',
                    height: '48px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    background: isScreenSharing ? '#bf5af2' : 'rgba(255, 255, 255, 0.15)',
                    transition: 'background 0.2s ease'
                  }}
                  title={isScreenSharing ? 'Stop sharing' : 'Share screen'}
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 18c1.1 0 1.99-.9 1.99-2L22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2H0v2h24v-2h-4zM4 6h16v10H4V6z" fill="white"/>
                  </svg>
                </motion.div>
              </FeatureGate>

              {/* Divider before end button */}
              <div style={{ width: '1px', height: '32px', background: 'rgba(255,255,255,0.15)' }} />

              {/* End Session button */}
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleEndSession}
                style={{
                  cursor: 'pointer',
                  width: '48px',
                  height: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  background: '#ff453a',
                  boxShadow: '0 0 12px rgba(255, 69, 58, 0.4)'
                }}
                title="End session"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08c-.18-.17-.29-.42-.29-.7 0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.28-.79-.74-1.69-1.36-2.67-1.85-.33-.16-.56-.5-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z" fill="white"/>
                </svg>
              </motion.div>
            </div>
          )}

          {/* VideoCollaborationPanel is rendered inside LiveKitVoiceInput (inside Room context) */}
        </motion.div>

        {/* Footer - Hidden during active calls (2025 UX: focus on conversation).
            Also hidden in focus layout: it's position:fixed and sat on top of
            the chat input in Silent Mode (isCallActive stays false there). */}
        {!isCallActive && !focusChat && (
        <div className="tutor-footer" style={{
          position: 'fixed',
          bottom: '0',
          left: '0',
          right: '0',
          backgroundColor: isDarkMode ? '#3D4954' : '#F5F9FA',
          borderTop: isDarkMode ? '1px solid #3D4954' : '1px solid #D5DEDD',
          padding: '12px 16px',
          textAlign: 'center',
          fontSize: '13px',
          color: '#8D7F88',
          zIndex: 90,
          transition: 'background-color 0.3s ease, border-color 0.3s ease',
        }}>
          {/* Minimal single-line footer - 2025 style */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            flexWrap: 'wrap',
          }}>
            <span>© 2026 Tappy</span>
            <span style={{ color: isDarkMode ? '#76909F' : '#ABCACA' }}>·</span>
            <a
              href="/terms-of-service.html"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: isDarkMode ? '#ABCACA' : '#76909F',
                textDecoration: 'none',
                transition: 'color 0.15s ease',
              }}
              onMouseEnter={(e) => e.target.style.color = isDarkMode ? '#FCFDFE' : '#3D4954'}
              onMouseLeave={(e) => e.target.style.color = isDarkMode ? '#ABCACA' : '#76909F'}
            >
              Terms
            </a>
            <span style={{ color: isDarkMode ? '#76909F' : '#ABCACA' }}>·</span>
            <a
              href="/privacy-policy.html"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: isDarkMode ? '#ABCACA' : '#76909F',
                textDecoration: 'none',
                transition: 'color 0.15s ease',
              }}
              onMouseEnter={(e) => e.target.style.color = isDarkMode ? '#FCFDFE' : '#3D4954'}
              onMouseLeave={(e) => e.target.style.color = isDarkMode ? '#ABCACA' : '#76909F'}
            >
              Privacy
            </a>
          </div>
        </div>
        )}
      </div>

      {/* Session Feedback Modal removed */}


      {/* Achievement Share Toast */}
      <AnimatePresence>
        {showAchievementToast && (
          <motion.div
            className="achievement-toast"
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 50, scale: 0.9 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            onClick={() => {
              // Simple achievement sharing - just copy a celebratory message
              const celebratory = `Just had a breakthrough moment! 🎉✨ Check out my AI tutor session: ${window.location.href}`;
              navigator.clipboard.writeText(celebratory);
              setShowAchievementToast(false);
            }}
          >
            <div className="toast-content">
              <span className="toast-emoji">🎉</span>
              <div className="toast-text">
                <strong>Breakthrough!</strong>
                <span>Share it?</span>
              </div>
              <span className="toast-arrow">📤</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Inline styles for friend collaboration features */}
      <style jsx>{`
        
        /* Achievement Toast Styles */
        .achievement-toast {
          position: fixed;
          bottom: 100px;
          right: 24px;
          background: linear-gradient(135deg, #e67e22, #f39c12);
          color: white;
          border-radius: 16px;
          padding: 16px 20px;
          box-shadow: 0 8px 32px rgba(230, 126, 34, 0.4);
          cursor: pointer;
          z-index: 1001;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          max-width: 280px;
        }

        .toast-content {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .toast-emoji {
          font-size: 1.5rem;
          animation: bounce 1s ease infinite;
        }

        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-8px); }
          60% { transform: translateY(-4px); }
        }

        .toast-text {
          display: flex;
          flex-direction: column;
          gap: 2px;
          flex: 1;
        }

        .toast-text strong {
          font-size: 0.9rem;
          font-weight: 600;
        }

        .toast-text span {
          font-size: 0.8rem;
          opacity: 0.9;
        }

        .toast-arrow {
          font-size: 1.2rem;
          opacity: 0.8;
        }

        .achievement-toast:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 40px rgba(230, 126, 34, 0.5);
        }

        .achievement-toast:active {
          transform: translateY(0);
        }

        @media (max-width: 768px) {
          .achievement-toast {
            bottom: 80px;
            right: 16px;
            left: 16px;
            max-width: none;
          }

        }
      `}</style>

      {/* Neo Setup Modal removed */}

      {/* Toast Notification */}
      {showToast && toastMessage && (
        <ToastNotification
          message={toastMessage}
          type={toastType}
          show={showToast}
          onClose={handleToastClose}
          duration={toastActionButton ? 13000 : 4000}
          actionButton={toastActionButton}
        />
      )}

      {/* Guest Progress Protection Modal - Investment Psychology */}
      <AnimatePresence>
        {isGuest && showProgressProtection && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2100,
              padding: TappyLabsBranding.typography.scale.md
            }}
            onClick={() => {
              setShowProgressProtection(false);
              setProgressProtectionDismissed(true); // Don't show again this session
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 50 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                ...TappyLabsBranding.components.aquariumContainer,
                padding: TappyLabsBranding.typography.scale['2xl'],
                maxWidth: '480px',
                width: '100%',
                background: TappyLabsBranding.colors.gradients.crystalClear,
                textAlign: 'center',
                position: 'relative'
              }}
            >
              {/* Close button */}
              <button
                onClick={() => {
                  setShowProgressProtection(false);
                  setProgressProtectionDismissed(true); // Don't show again this session
                }}
                style={{
                  position: 'absolute',
                  top: TappyLabsBranding.typography.scale.md,
                  right: TappyLabsBranding.typography.scale.md,
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: TappyLabsBranding.colors.semantic.neutral,
                  padding: TappyLabsBranding.typography.scale.xs
                }}
              >
                ×
              </button>

              {/* Warning Icon Animation */}
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                style={{
                  fontSize: '4rem',
                  marginBottom: TappyLabsBranding.typography.scale.lg,
                  color: TappyLabsBranding.colors.semantic.warning
                }}
              >
                ⚠️
              </motion.div>

              {/* Header */}
              <h2 style={{
                color: TappyLabsBranding.colors.primary.deepOcean,
                fontSize: TappyLabsBranding.typography.scale['3xl'],
                fontWeight: TappyLabsBranding.typography.weights.bold,
                margin: `0 0 ${TappyLabsBranding.typography.scale.lg} 0`,
                fontFamily: TappyLabsBranding.typography.fonts.headers
              }}>
                Don't Lose Your Progress!
              </h2>

              {/* Investment Summary */}
              <div style={{
                ...TappyLabsBranding.components.glassCard,
                padding: TappyLabsBranding.typography.scale.lg,
                marginBottom: TappyLabsBranding.typography.scale.lg,
                background: TappyLabsBranding.colors.transparency.subtle
              }}>
                <div style={{
                  fontSize: TappyLabsBranding.typography.scale.xl,
                  fontWeight: TappyLabsBranding.typography.weights.bold,
                  color: TappyLabsBranding.colors.primary.learningAquariumBlue,
                  marginBottom: TappyLabsBranding.typography.scale.sm
                }}>
                  Your Learning Investment:
                </div>
                <div style={{
                  fontSize: TappyLabsBranding.typography.scale.lg,
                  color: TappyLabsBranding.colors.primary.deepOcean,
                  fontWeight: TappyLabsBranding.typography.weights.medium
                }}>
                  {tokensEarnedThisSession} tokens earned • {sessionInteractionCount} interactions completed
                </div>
                <div style={{
                  fontSize: TappyLabsBranding.typography.scale.sm,
                  color: TappyLabsBranding.colors.semantic.neutral,
                  marginTop: TappyLabsBranding.typography.scale.xs
                }}>
                  Estimated value: ${(tokensEarnedThisSession * 0.10).toFixed(2)} of academic development
                </div>
              </div>

              {/* Risk Message */}
              <p style={{
                color: TappyLabsBranding.colors.primary.deepOcean,
                fontSize: TappyLabsBranding.typography.scale.lg,
                margin: `0 0 ${TappyLabsBranding.typography.scale.lg} 0`,
                lineHeight: TappyLabsBranding.typography.lineHeights.relaxed
              }}>
                <strong>Without an account, you'll lose everything when you close this browser.</strong>
                <br />
                Your learning progress, token balance, and academic achievements will disappear.
              </p>

              {/* Benefits List */}
              <div style={{
                textAlign: 'left',
                marginBottom: TappyLabsBranding.typography.scale.xl,
                ...TappyLabsBranding.components.glassCard,
                padding: TappyLabsBranding.typography.scale.lg
              }}>
                <h3 style={{
                  color: TappyLabsBranding.colors.secondary.ecosystemGreen,
                  fontSize: TappyLabsBranding.typography.scale.lg,
                  fontWeight: TappyLabsBranding.typography.weights.bold,
                  margin: `0 0 ${TappyLabsBranding.typography.scale.md} 0`,
                  textAlign: 'center'
                }}>
                  Create Account to Protect:
                </h3>
                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  fontSize: TappyLabsBranding.typography.scale.base,
                  color: TappyLabsBranding.colors.primary.deepOcean
                }}>
                  <li style={{ marginBottom: TappyLabsBranding.typography.scale.sm, display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: TappyLabsBranding.typography.scale.sm, color: TappyLabsBranding.colors.primary.confidenceFishGold }}>•</span>
                    Your {tokensEarnedThisSession} token balance and earning history
                  </li>
                  <li style={{ marginBottom: TappyLabsBranding.typography.scale.sm, display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: TappyLabsBranding.typography.scale.sm, color: TappyLabsBranding.colors.primary.confidenceFishGold }}>•</span>
                    Multi-subject learning progress and achievements
                  </li>
                  <li style={{ marginBottom: TappyLabsBranding.typography.scale.sm, display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: TappyLabsBranding.typography.scale.sm, color: TappyLabsBranding.colors.primary.confidenceFishGold }}>•</span>
                    Access to portfolio building and mentorship opportunities
                  </li>
                  <li style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: TappyLabsBranding.typography.scale.sm, color: TappyLabsBranding.colors.primary.confidenceFishGold }}>•</span>
                    Future collaboration networks and advanced features
                  </li>
                </ul>
              </div>

              {/* Action Button */}
              <button
                onClick={() => {
                  // Track progress protection action
                  try {
                    posthog.capture('progress_protection_accepted', {
                      tokens_earned: tokensEarnedThisSession,
                      interaction_count: sessionInteractionCount,
                      session_type: 'guest',
                      anonymous_id: anonymousId,
                      experience_type: 'investment_based'
                    });
                  } catch (e) {
                    console.error('PostHog tracking error:', e);
                  }

                  // Navigate to pricing page with context
                  const upgradeParams = new URLSearchParams({
                    tier: 'unlimited',
                    source: 'progress_protection',
                    anon: anonymousId,
                    tokens: tokensEarnedThisSession,
                    interactions: sessionInteractionCount
                  });

                  setShowProgressProtection(false);
                  setProgressProtectionDismissed(true);
                  navigate(`/pricing?${upgradeParams.toString()}`);
                }}
                style={{
                  ...TappyLabsBranding.components.buttons.primary,
                  fontSize: TappyLabsBranding.typography.scale.lg,
                  padding: `${TappyLabsBranding.typography.scale.md} ${TappyLabsBranding.typography.scale.xl}`,
                  fontFamily: TappyLabsBranding.typography.fonts.body,
                  fontWeight: TappyLabsBranding.typography.weights.semibold,
                  width: '100%'
                }}
              >
                Protect My Learning Investment
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Share with Parent Modal */}
      <AnimatePresence>
        {showShareWithParentModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              backdropFilter: 'blur(8px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000,
              padding: '20px'
            }}
            onClick={() => setShowShareWithParentModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'white',
                borderRadius: '20px',
                padding: '40px',
                maxWidth: '500px',
                width: '100%',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                textAlign: 'center'
              }}
            >
              <div style={{
                width: '64px',
                height: '64px',
                margin: '0 auto 20px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #297A92, #6DB4CD)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
              </div>
              <h2 style={{
                fontSize: '24px',
                fontWeight: '700',
                color: '#0F172A',
                marginBottom: '16px'
              }}>
                Unlock Study Sessions with Friends
              </h2>
              <p style={{
                fontSize: '16px',
                color: '#64748B',
                marginBottom: '24px',
                lineHeight: '1.6'
              }}>
                Get instant access with Apple Pay, Google Pay, Cash App, or share with a parent
              </p>

              {/* Error Display */}
              {paymentError && (
                <div style={{
                  background: '#FEE2E2',
                  border: '1px solid #FCA5A5',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '16px',
                  color: '#991B1B',
                  fontSize: '14px'
                }}>
                  {paymentError}
                </div>
              )}

              {/* Quick Payment Options - Ultra Low Friction */}
              <div style={{ marginBottom: '24px' }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#64748B',
                  marginBottom: '12px',
                  textAlign: 'left'
                }}>
                  Pay now (one tap):
                </div>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                  {/* Apple Pay */}
                  <button
                    onClick={() => handleGuestCheckout('applepay')}
                    disabled={paymentLoading}
                    style={{
                      flex: 1,
                      padding: '14px',
                      background: paymentLoading ? '#666' : 'black',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: paymentLoading ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      opacity: paymentLoading ? 0.6 : 1
                    }}
                  >
                    {paymentLoading ? '...' : <><span style={{ fontSize: '20px' }}>🍎</span> Apple Pay</>}
                  </button>

                  {/* Google Pay */}
                  <button
                    onClick={() => handleGuestCheckout('googlepay')}
                    disabled={paymentLoading}
                    style={{
                      flex: 1,
                      padding: '14px',
                      background: 'white',
                      color: paymentLoading ? '#999' : '#3C4043',
                      border: '1px solid #DADCE0',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: paymentLoading ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      opacity: paymentLoading ? 0.6 : 1
                    }}
                  >
                    {paymentLoading ? '...' : <><span style={{ fontSize: '20px' }}>💳</span> Google Pay</>}
                  </button>
                </div>

                {/* Cash App */}
                <button
                  onClick={() => handleGuestCheckout('cashapp')}
                  disabled={paymentLoading}
                  style={{
                    width: '100%',
                    padding: '14px',
                    background: paymentLoading ? '#999' : 'linear-gradient(135deg, #00D632 0%, #00B828 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: paymentLoading ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    marginBottom: '16px',
                    opacity: paymentLoading ? 0.6 : 1
                  }}
                >
                  {paymentLoading ? 'Starting checkout...' : <><span style={{ fontSize: '20px' }}>💸</span> Cash App</>}
                </button>

                {/* Divider */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  margin: '20px 0'
                }}>
                  <div style={{ flex: 1, height: '1px', background: '#E2E8F0' }}></div>
                  <span style={{ fontSize: '14px', color: '#94A3B8', fontWeight: '500' }}>or</span>
                  <div style={{ flex: 1, height: '1px', background: '#E2E8F0' }}></div>
                </div>

                <div style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#64748B',
                  marginBottom: '12px',
                  textAlign: 'left'
                }}>
                  Share with parent:
                </div>
              </div>

              {/* QR Code Display for Parent Scanning */}
              <div style={{
                background: 'white',
                borderRadius: '16px',
                padding: '24px',
                marginBottom: '20px',
                textAlign: 'center',
                border: 'none',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
              }}>
                {/* Instructions */}
                <div style={{
                  fontSize: '15px',
                  fontWeight: '600',
                  color: '#0F172A',
                  marginBottom: '16px'
                }}>
                  📱 Scan with your phone
                </div>

                {/* QR Code */}
                {shareCode && (
                  <div style={{
                    background: 'white',
                    padding: '20px',
                    borderRadius: '12px',
                    display: 'inline-block',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                  }}>
                    <QRCodeSVG
                      value={`${window.location.origin}/pay/${shareCode}`}
                      size={200}
                      level="H"
                      includeMargin={true}
                    />
                  </div>
                )}

                {/* Link below QR */}
                <div style={{
                  marginTop: '16px',
                  fontSize: '13px',
                  color: '#64748B',
                  fontFamily: 'monospace'
                }}>
                  teachtappy.ai/pay/{shareCode}
                </div>

                {/* Progress info */}
                <div style={{
                  marginTop: '12px',
                  fontSize: '14px',
                  color: '#10B981',
                  fontWeight: '600'
                }}>
                  {tokensEarnedThisSession > 0 && `✨ ${tokensEarnedThisSession} tokens earned`}
                </div>
              </div>

              {/* Instructions */}
              <div style={{
                background: '#F1F5F9',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '16px',
                fontSize: '14px',
                color: '#475569',
                lineHeight: '1.6'
              }}>
                <strong>How it works:</strong>
                <ol style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                  <li>Pull out your phone</li>
                  <li>Open camera and scan QR code</li>
                  <li>Text the link to your parent</li>
                  <li>Parent taps link → Unlocks instantly!</li>
                </ol>
              </div>

              {/* Close Button */}
              <button
                onClick={() => setShowShareWithParentModal(false)}
                style={{
                  width: '100%',
                  padding: '14px',
                  background: '#F1F5F9',
                  color: '#64748B',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.background = '#E2E8F0'}
                onMouseLeave={(e) => e.target.style.background = '#F1F5F9'}
              >
                Close
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Join Friend Modal - FaceTime-style code entry for joining a SESSION */}
      <JoinFriendModal
        isOpen={showJoinFriendModal}
        onClose={() => setShowJoinFriendModal(false)}
        user={user}
      />

      {/* Add Friend Modal - FaceTime-style code entry for adding a FRIEND */}
      <AddFriendModal
        isOpen={showAddFriendModal}
        onClose={() => setShowAddFriendModal(false)}
        onFriendAdded={(friend) => {
          // Show success toast
          setToastMessage(`Friend request sent to ${friend.display_name}!`);
          setToastType('success');
          setShowToast(true);
        }}
      />

      {/* Study Code Sheet - 2025 FaceTime/Cash App style sharing experience */}
      <StudyCodeSheet
        isOpen={showStudyCodeSheet}
        onClose={() => setShowStudyCodeSheet(false)}
        code={studyCodeData?.code}
        expiresAt={studyCodeData?.expiresAt}
        joinUrl={studyCodeData?.joinUrl}
        hostName={user?.name || user?.username || 'You'}
        subject="Study Session"
        onRegenerateCode={handleInviteFriend}
      />

      {/* Token Wallet Modal */}
      <TokenWalletModal
        isOpen={showTokenWallet}
        onClose={() => setShowTokenWallet(false)}
        user={user}
        userTier={userTier}
        hasFeatureAccess={hasFeatureAccess}
      />

      {/* Session Summary Modal - Duolingo-style token celebration */}
      <CollaborationCelebration
        isVisible={showSessionSummary}
        onClose={() => {
          setShowSessionSummary(false);
          setSessionSummaryData(null);
        }}
        sessionDuration={sessionSummaryData?.sessionDuration || 0}
        isAuthenticated={isAuthenticated}
        showTokens={true}
        tokensEarned={sessionSummaryData?.tokens_earned || 0}
        breakdown={sessionSummaryData?.breakdown || null}
        mysteryBonus={sessionSummaryData?.mystery_bonus || 0}
        quests={sessionSummaryData?.quests || null}
        streakDays={sessionSummaryData?.streak_days || 0}
        newBalance={sessionSummaryData?.new_balance || 0}
        // Study buddy props (Snapchat model - December 2025)
        studyPartner={sessionSummaryData?.studyPartner || null}
        sessionId={sessionSummaryData?.sessionId || null}
      />

      {/* Learning Snapshot - Post-session investment visibility (Conversion funnel) */}
      <LearningSnapshot
        isOpen={showLearningSnapshot}
        onClose={() => setShowLearningSnapshot(false)}
        onContinue={() => setShowLearningSnapshot(false)}
        sessionNumber={guestSessionCount}
        totalFreeSessions={isAnonymous ? 3 : 4}
        learningStyle={guestProfile.learningStyleWeights || learningStyleWeights}
        topicsCovered={sessionTopics}
        cognitiveState={sessionCognitiveState}
        miraOrbColor={miraOrbColor}
        isAnonymous={isAnonymous}
        anonymousId={anonymousId || localStorage.getItem('morpheus_anon_id') || ''}
        // Accumulated investment data (persistence)
        totalSessions={guestProfile.totalSessions || guestSessionCount}
        totalMinutes={guestProfile.totalMinutes || 0}
        streakDays={guestProfile.streakDays || 0}
        allTopicsCovered={guestProfile.topicsCovered || []}
        onCreateAccount={() => {
          setShowLearningSnapshot(false);
          // Pass anonymousId so Login can transfer guest progress and link sessions
          const anonId = anonymousId || localStorage.getItem('morpheus_anon_id') || '';
          navigate(`/login?mode=signup&source=learning_snapshot&anon=${encodeURIComponent(anonId)}`);
        }}
        onStartOver={() => {
          // Clear local storage anonymous ID to start fresh
          localStorage.removeItem('morpheus_anon_id');
          setShowLearningSnapshot(false);
          window.location.reload();
        }}
      />

      {/* Customize Mira Prompt - Session 2 free orb color picker (Conversion funnel) */}
      <CustomizeMiraPrompt
        isOpen={showCustomizeMiraPrompt}
        onClose={() => setShowCustomizeMiraPrompt(false)}
        anonymousId={anonymousId || localStorage.getItem('morpheus_anon_id') || ''}
        onSelectColor={async (colorId) => {
          // Save the selected color locally
          setMiraOrbColor(colorId);
          localStorage.setItem('mira_orb_color', colorId);
          setShowCustomizeMiraPrompt(false);
          // TODO: If user is authenticated, persist to backend
        }}
        currentColor={miraOrbColor}
      />

      {/* Teach It Back - Full screen Feynman technique experience */}
      <AnimatePresence>
        {showTeachItBack && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'white',
              zIndex: 10000,
              overflow: 'auto',
            }}
          >
            <TeachItBackView
              user={user}
              onClose={() => setShowTeachItBack(false)}
              onStartVoice={(config) => {
                // Start voice session with "confused student" Mira
                // Set teach_it_back mode and topic BEFORE starting voice
                // Use subtitle (academic name) if available for API consistency
                setTeachItBackActive(true);
                setCurrentTeachTopic(config.topic?.subtitle || config.topic?.name || config.topic);
                setShowTeachItBack(false);
                setCurrentMode('voice');
                playActivationSound();
                setConnectionState('connecting');
                startTransition(() => {
                  setIsCallActive(true);
                  if (!voiceComponentInitialized) setVoiceComponentInitialized(true);
                });
              }}
              onScoreSubmit={async (score) => {
                // Award tokens for good explanation
                if (score.tokensEarned > 0) {
                  // Update local balance immediately for responsiveness
                  setTokenBalance(prev => prev + score.tokensEarned);
                  showToastNotification(`+${score.tokensEarned} tokens for teaching!`, 'success');

                  // Call backend to persist token award
                  if (user?.id) {
                    try {
                      await axios.post('/api/voice/award-teaching-tokens', {
                        user_id: user.id,
                        topic: currentTeachTopic,
                        score: score.score,
                        tokens_earned: score.tokensEarned
                      });
                    } catch (err) {
                      console.warn('Failed to persist teaching tokens:', err);
                      // Local balance already updated, so user sees the reward
                    }
                  }
                }
                // Reset teach_it_back mode after scoring
                setTeachItBackActive(false);
                setCurrentTeachTopic(null);
              }}
              isVoiceActive={connectionState === 'connected'}
              agentState={agentState}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Clutch Time Proactive Trigger (Schoology Integration - Phase 2) */}
      {/* Only show when not in active session and not already in Clutch Time flow */}
      {connectionState === 'idle' && !showClutchTimeModal && !showClutchTimeRadar && !showClutchTimeSummary && !clutchTimeActive && (
        <ClutchTimeProactiveTrigger
          onActivateClutchTime={(priorityTest) => {
            // Pre-fill with detected test data from Schoology
            if (priorityTest) {
              setClutchTimeSubject(priorityTest.subject || '');
              setClutchTimeTestDate(priorityTest.due_date ? priorityTest.due_date.split('T')[0] : '');
            }
            setShowClutchTimeModal(true);
          }}
          onDismiss={() => {
            // Trigger dismissed - logged via API, nothing else needed
            console.log('Clutch Time trigger dismissed');
          }}
        />
      )}

      {/* Clutch Time Entry Modal */}
      <ClutchTimeModal
        isOpen={showClutchTimeModal}
        onClose={() => setShowClutchTimeModal(false)}
        onStart={({ subject, testDate, importedTopics = [], importSource = 'manual' }) => {
          setClutchTimeSubject(subject);
          setClutchTimeTestDate(testDate);
          setClutchTimeImportedTopics(importedTopics);
          setClutchTimeImportSource(importSource);
          setShowClutchTimeModal(false);
          setShowClutchTimeRadar(true);
        }}
      />

      {/* Clutch Time Radar View */}
      <AnimatePresence>
        {showClutchTimeRadar && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              backdropFilter: 'blur(4px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000,
              padding: '20px'
            }}
            onClick={() => setShowClutchTimeRadar(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ClutchTimeRadar
                subject={clutchTimeSubject}
                testDate={clutchTimeTestDate}
                importedTopics={clutchTimeImportedTopics}
                onClose={() => setShowClutchTimeRadar(false)}
                onStartStudying={(sessionId, radarData) => {
                  setClutchTimeSessionId(sessionId);
                  setShowClutchTimeRadar(false);

                  // Set clutch_time mode and topics BEFORE starting voice (like Teach It Back pattern)
                  setClutchTimeActive(true);
                  // Extract weak topics from radarData (focus and review sections)
                  // Also include imported topics if they exist
                  const weakTopics = [
                    ...(radarData?.topics?.focus || []),
                    ...(radarData?.topics?.review || []),
                    ...(clutchTimeImportedTopics || []).filter(t =>
                      !radarData?.topics?.focus?.some(f => f.name === t.name) &&
                      !radarData?.topics?.review?.some(r => r.name === t.name)
                    )
                  ];
                  setClutchTimeTopics(weakTopics);

                  if (clutchTimeLaunchedFromSilent.current) {
                    // Return to Silent Mode with Clutch Time context
                    clutchTimeLaunchedFromSilent.current = false;
                    setIsSilentMode(true);
                    setConnectionState('connected');
                  } else {
                    // Start voice session for studying
                    setCurrentMode('voice');
                    playActivationSound();
                    setConnectionState('connecting');
                    startTransition(() => {
                      setIsCallActive(true);
                      if (!voiceComponentInitialized) setVoiceComponentInitialized(true);
                    });
                  }
                }}
                onTellTopics={() => {
                  // Close radar and start voice to tell topics
                  setShowClutchTimeRadar(false);
                  setCurrentMode('voice');
                  playActivationSound();
                  setConnectionState('connecting');
                  startTransition(() => {
                    setIsCallActive(true);
                    if (!voiceComponentInitialized) setVoiceComponentInitialized(true);
                  });
                }}
                onScreenShare={() => {
                  // Close radar and start voice session, then trigger screen share after connected
                  setShowClutchTimeRadar(false);
                  setCurrentMode('voice');
                  playActivationSound();
                  setConnectionState('connecting');
                  setPendingScreenShare(true); // Will trigger screen share once room is connected
                  startTransition(() => {
                    setIsCallActive(true);
                    if (!voiceComponentInitialized) setVoiceComponentInitialized(true);
                  });
                }}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Clutch Time Session Summary */}
      <AnimatePresence>
        {showClutchTimeSummary && clutchTimeSessionId && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              backdropFilter: 'blur(4px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000,
              padding: '20px'
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
            >
              <ClutchTimeSummary
                sessionId={clutchTimeSessionId}
                onDone={() => {
                  setShowClutchTimeSummary(false);
                  setClutchTimeSessionId(null);
                  setClutchTimeSubject('');
                  setClutchTimeTestDate('');
                  // Also reset active state and topics
                  setClutchTimeActive(false);
                  setClutchTimeTopics([]);
                  setClutchTimeImportedTopics([]);
                  setClutchTimeImportSource('manual');
                }}
                onKeepStudying={() => {
                  setShowClutchTimeSummary(false);
                  setShowClutchTimeRadar(true);
                }}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Screen Share Prompt - triggered from Clutch Time "Share your screen" */}
      {/* Browser requires user gesture for getDisplayMedia, so we show this prompt */}
      <AnimatePresence>
        {showScreenSharePrompt && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              backdropFilter: 'blur(4px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10001,
              padding: '20px'
            }}
            onClick={() => setShowScreenSharePrompt(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'linear-gradient(135deg, #1e293b, #0f172a)',
                borderRadius: '16px',
                padding: '32px',
                maxWidth: '320px',
                textAlign: 'center',
                border: 'none',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
              }}
            >
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2), rgba(217, 119, 6, 0.1))',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px'
              }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2">
                  <rect x="2" y="3" width="20" height="14" rx="2"/>
                  <line x1="8" y1="21" x2="16" y2="21"/>
                  <line x1="12" y1="17" x2="12" y2="21"/>
                </svg>
              </div>
              <h3 style={{
                color: 'white',
                fontSize: '18px',
                fontWeight: 600,
                marginBottom: '8px'
              }}>
                Share Your Screen
              </h3>
              <p style={{
                color: '#94a3b8',
                fontSize: '14px',
                marginBottom: '24px',
                lineHeight: 1.5
              }}>
                Show Mira your homework or study materials so she can help you better.
              </p>
              <button
                onClick={handleScreenSharePromptClick}
                style={{
                  width: '100%',
                  padding: '14px 24px',
                  background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                  border: 'none',
                  borderRadius: '12px',
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'scale(1.02)';
                  e.target.style.boxShadow = '0 8px 24px rgba(245, 158, 11, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'scale(1)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="3" width="20" height="14" rx="2"/>
                  <line x1="8" y1="21" x2="16" y2="21"/>
                  <line x1="12" y1="17" x2="12" y2="21"/>
                </svg>
                Start Sharing
              </button>
              <button
                onClick={() => setShowScreenSharePrompt(false)}
                style={{
                  marginTop: '12px',
                  background: 'none',
                  border: 'none',
                  color: '#64748b',
                  fontSize: '14px',
                  cursor: 'pointer',
                  padding: '8px'
                }}
              >
                Maybe later
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Guest Session Limit Paywall Modal */}
      <AnimatePresence>
        {showUpgradeModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000,
              backdropFilter: 'blur(4px)'
            }}
            onClick={() => { setShowUpgradeModal(false); setCheckoutError(''); setCheckoutLoading(false); }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                width: '95vw',
                maxWidth: '900px',
                maxHeight: '90vh',
                overflow: 'auto',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                position: 'relative'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={() => { setShowUpgradeModal(false); setCheckoutError(''); setCheckoutLoading(false); }}
                style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  background: '#f3f4f6',
                  border: 'none',
                  borderRadius: '50%',
                  width: '36px',
                  height: '36px',
                  fontSize: '20px',
                  color: '#6b7280',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10
                }}
              >
                ✕
              </button>

              {/* Investment-Focused Header */}
              <div style={{
                padding: '24px 24px 0',
                textAlign: 'center'
              }}>
                {/* Mira orb with user's custom color */}
                <div style={{
                  width: '56px',
                  height: '56px',
                  margin: '0 auto 16px',
                  borderRadius: '50%',
                  background: miraOrbColor && miraOrbColor !== 'default'
                    ? {
                        coral: 'linear-gradient(135deg, #FF6B6B, #FF8E8E)',
                        ocean: 'linear-gradient(135deg, #4ECDC4, #6EE7DF)',
                        sunset: 'linear-gradient(135deg, #FF9F43, #FFB76B)',
                        midnight: 'linear-gradient(135deg, #5F27CD, #7C4DFF)',
                        forest: 'linear-gradient(135deg, #26DE81, #4AE590)',
                        lavender: 'linear-gradient(135deg, #A55EEA, #C084FC)'
                      }[miraOrbColor] || 'linear-gradient(135deg, #297A92, #42909E)'
                    : 'linear-gradient(135deg, #297A92, #42909E)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 8px 20px rgba(41, 122, 146, 0.25)'
                }}>
                  <div style={{ display: 'flex', gap: '10px', marginTop: '-4px' }}>
                    <div style={{ width: '7px', height: '7px', backgroundColor: 'white', borderRadius: '50%' }} />
                    <div style={{ width: '7px', height: '7px', backgroundColor: 'white', borderRadius: '50%' }} />
                  </div>
                </div>

                <h2 style={{
                  fontSize: '22px',
                  fontWeight: 700,
                  color: '#111827',
                  margin: 0
                }}>
                  Keep your personalized Mira?
                </h2>
                <p style={{
                  fontSize: '15px',
                  color: '#6b7280',
                  marginTop: '8px',
                  marginBottom: '12px'
                }}>
                  She knows how you learn best
                </p>

                {/* Compact learning insights - what they'd lose */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '8px',
                  flexWrap: 'wrap',
                  marginBottom: '8px'
                }}>
                  {learningStyleWeights && Object.entries(learningStyleWeights)
                    .filter(([_, value]) => value > 30)
                    .slice(0, 2)
                    .map(([style, value]) => (
                      <span key={style} style={{
                        padding: '5px 10px',
                        backgroundColor: '#f0fdf4',
                        color: '#166534',
                        borderRadius: '100px',
                        fontSize: '12px',
                        fontWeight: 500
                      }}>
                        {style === 'visual' ? 'Visual' :
                         style === 'auditory' ? 'Auditory' :
                         style === 'stepByStep' ? 'Step-by-step' : style} learner
                      </span>
                    ))
                  }
                  {sessionInteractionCount > 0 && (
                    <span style={{
                      padding: '5px 10px',
                      backgroundColor: '#eff6ff',
                      color: '#1d4ed8',
                      borderRadius: '100px',
                      fontSize: '12px',
                      fontWeight: 500
                    }}>
                      {sessionInteractionCount}+ interactions learned
                    </span>
                  )}
                </div>
              </div>

              {/* Pricing Tiers */}
              {checkoutError && (
                <div style={{
                  margin: '0 24px 12px',
                  padding: '10px 16px',
                  backgroundColor: '#fef2f2',
                  color: '#dc2626',
                  borderRadius: '8px',
                  fontSize: '14px',
                  textAlign: 'center'
                }}>
                  {checkoutError}
                </div>
              )}
              <PricingTiers
                userTier={userTier}
                currentUser={user}
                onDirectCheckout={handleDirectCheckout}
                loading={checkoutLoading}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default TutorInterface;